<template>
  <div class="SparkIgnition" :class="{ 'is-mobile': isMobile }">
    <InitializingSpinner v-if="!isCityInitialized" class="slide" />

    <template v-else>
      <div
        v-if="showResultDayToc && showSparkContent"
        :class="['results-day-toc', { 'is-mobile': isMobile, 'is-loading': sparkStep === SparkStep.WIZARD }]"
        aria-label="Next seven result days"
      >
        <div
          :class="['results-day-toc-week', { 'is-active': isWeekTocChipActive, 'has-activity': hasWeekActivity }]"
          @click="handleResultDayTocClick(WEEK_TOC_DATE)"
        >
          <GamIcon :name="GamIconName.SPARK" size="14px" is-current />
          <span class="toc-day-letter">{{ localize('sparks.toc.week') }}</span>
        </div>

        <div ref="resultDayTocDaysRef" class="results-day-toc-days">
          <div
            v-for="day in resultDayTocEntries"
            :key="`toc-${day.date}`"
            :data-toc-date="day.date"
            :class="[
              'results-day-toc-item',
              {
                'is-active': !isWeekTocChipActive && day.date === dateParam,
                'is-disabled': day.isDisabled,
                'is-spark': day.isSpark,
              },
            ]"
            :style="{ opacity: tocItemOpacity(day.date) }"
            @click="handleResultDayTocClick(day.date)"
          >
            <span class="toc-day-letter">{{ day.label }}</span>
            <span class="toc-day-number">{{ day.dayNumber }}</span>
          </div>
        </div>

        <button class="results-day-toc-availability" @click="handleAvailabilityClick">
          <GamIcon :name="GamIconName.TIME" :size="GamIconSize.NORMAL" is-current />
        </button>
      </div>

      <div v-if="sparkCityLabel" ref="sparkCityLabelRef" class="spark-city-label">
        <div class="spark-city-label-text" @click="toggleCitySearch">{{ sparkCityLabel }}</div>
        <SparkMatchCity
          v-if="isCitySearchOpen"
          class="spark-city-search"
          :on-change="handleSparkCitySelected"
          :max-items="3"
          :key="`spark-city-${citySearchResetKey}`"
        />
      </div>

      <InitializingSpinner v-if="isCitySwitchLoading" class="slide city-switch-loader" />

      <div
        v-if="!showNoLocationsCard && !isCitySwitchLoading"
        id="wizard"
        :class="['wizard-slides', { 'is-active': sparkStep === SparkStep.WIZARD }]"
      >
        <div id="loader" class="wizard-slide spark-card-slide result-slide loader-slide">
          <SkeletonCard class="loader-card spark-card" />
        </div>
      </div>

      <div v-if="showNoLocationsCard && !isCitySwitchLoading" class="slide spark-card-slide result-slide">
        <div class="results-card-layout">
          <NoLocationsCard />
        </div>
      </div>

      <div v-if="showEmptyReloadCard && !isCitySwitchLoading" class="slide spark-card-slide result-slide">
        <div class="results-card-layout">
          <div class="spark-card day-placeholder-card empty-reload-card">
            <div class="empty-reload-content">
              <div class="day-placeholder-title">{{ localize('sparks.empty_reload.title') }}</div>
              <div class="day-placeholder-subtitle">{{ localize('sparks.empty_reload.subtitle') }}</div>
              <GamButton
                :variant="GamButtonVariant.STEKLO"
                :size="GamButtonSize.SMALL"
                :is-loading="isReloadingSparks"
                :is-disabled="isReloadingSparks"
                is-full-size
                is-centered
                @click="handleReloadSparks"
              >
                {{ localize('sparks.empty_reload.action') }}
              </GamButton>
            </div>
          </div>
        </div>
      </div>

      <Transition name="new-results-fade">
        <div
          v-if="showNewResultsAbove && sparkStep === SparkStep.RESULTS"
          class="new-results-above"
          @click="handleScrollToNewResults"
        >
          <GamIcon :name="GamIconName.ARROW_UP" size="14px" is-current />
          <span>{{ localize('sparks.new_results_above') }}</span>
        </div>
      </Transition>

      <ScrollSnap
        v-if="sparkIntent && showSparkContent && !isCitySwitchLoading"
        id="results"
        :class="['spark-slides', { 'is-active': sparkStep === SparkStep.RESULTS }]"
        :scroll-snap-type="ScrollSnapType.Y_MANDATORY"
        :scroll-axis="isScrollFrozen ? ScrollAxis.Y_HIDDEN : ScrollAxis.Y_SCROLL"
        :slide-count="resultDeck.length"
        @on-entered="handleEnteredSlide"
        @on-snapped="handleSnappedSlide"
      >
        <SnapSlide v-for="card in resultDeck" :id="card.domId" :key="card.key" :class="card.classList">
          <div class="results-card-layout">
            <template v-if="card.kind === 'week'">
              <SparkWeekPreview
                :preview="weekPreview"
                :is-loading="isWeekPreviewLoading"
                :area-label="weekPreviewAreaLabel"
                :attended-gambits="attendedGambits"
                @select="handleWeekPreviewSelect"
                @play="handleWeekPreviewPlay"
              />
            </template>

            <template v-else-if="['loading', 'prev_loader', 'next_loader'].includes(card.kind)">
              <SkeletonCard />
            </template>

            <template v-else-if="card.kind === 'disabled' && card.intent">
              <DisabledCard
                :date="format(card.intent.date, 'EEEE, d LLL')"
                @click-availabilities="handleAvailabilityClick"
              />
            </template>

            <template v-else-if="card.kind === 'gambit' && card.intent && card.gambit">
              <GambitCard
                class="spark-card gambit-card"
                :now="nowTime"
                :expires-at="card.expiresAt!"
                :gambit="card.gambit"
                :intent="card.intent"
                @gambit-joined="handleGambitAttendanceChanged"
                @gambit-left="handleGambitAttendanceChanged"
              />
            </template>

            <template v-else-if="(card.kind === 'proposal' || card.kind === 'match') && card.intent && card.result">
              <SparkCard
                class="spark-card match-card"
                :now="nowTime"
                :expires-at="card.expiresAt!"
                :segment="card.result.segment"
                :proposal="getLatestSparkProposal(card.result.proposal, sparkProposal)"
                :intent="card.intent"
                @create-or-join-proposal="
                  (segment, startTime, endTime, chessSetCount) =>
                    handleCreateOrJoinProposalForIntent(card.intent!, segment, startTime, endTime, chessSetCount)
                "
                @accept-invitation="
                  (proposalId, chessSetCount) =>
                    handleAcceptInvitationForIntent(card.intent!, proposalId, chessSetCount)
                "
                @leave-proposal="
                  (proposalId, segment) => handleLeaveProposalForIntent(card.intent!, proposalId, segment)
                "
                @meet-anyways="(proposalId, segment) => handleMeetAnywaysForIntent(card.intent!, proposalId, segment)"
                @undo-meet-anyways="
                  (proposalId, segment) => handleUndoMeetAnywaysForIntent(card.intent!, proposalId, segment)
                "
              />
            </template>

            <template v-else-if="card.kind === 'proposal_missing' && card.intent">
              <NotFoundCard
                :date="format(card.intent.date, 'EEE, d LLL')"
                :is-loading="missingProposalState?.phase === 'result_reloading'"
                :reload-error="missingProposalState?.reloadError"
                :merged-into-gambit-id="proposalStore.mergedIntoGambitId"
                @click-reload="reloadLinkedProposalResult"
              />
            </template>

            <template v-else-if="card.kind === 'new' && card.intent">
              <CustomProposalCard
                :intent="card.intent"
                :intents="intents"
                :now="nowTime"
                @create-proposal="(newProposal) => handleCreateCustomProposalForIntent(card.intent!, newProposal)"
                class="spark-card custom-proposal-card"
              />
            </template>
          </div>
        </SnapSlide>
      </ScrollSnap>
    </template>

    <Transition name="swipe-hint-fade">
      <div v-if="showSwipeHint" class="swipe-hint">{{ localize('sparks.swipe_hint') }}</div>
    </Transition>

    <ScreenNav active="sparks" @profile="goToProfile" @map="goToMap">
      <template #left>
        <div
          :class="['nav-action-btn', { 'has-unread': hasStaleTocResults }]"
          :aria-label="localize('sparks.toc.refresh')"
          @click="navigationStore.refreshResults"
        >
          <GamIcon :name="GamIconName.REFRESH" is-current />
        </div>
      </template>
      <template #right>
        <div v-if="hasSparkableLocations" class="nav-action-btn" @click="navigationStore.gotoNewProposal">
          <GamIcon :name="GamIconName.PLUS" is-current />
        </div>
      </template>
    </ScreenNav>
  </div>

  <ChooseAvailability
    :is-open="isAvailabilityModalOpen"
    :on-confirm="handleConfirmAvailabilityModal"
    @close="handleCloseAvailabilityModal"
  />
</template>

<script lang="ts">
export default { name: 'SparkIgnition' };
export enum SparkScreen {
  DAYS = 'days',
  EDIT = 'edit',
  PLACES = 'places',
  RESULTS = 'results',
}
</script>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GambitType } from '@/core/data/gambit/gambit.type';
import type {
  CreateProposalDto,
  SegmentProposal,
  SegmentResult,
  SparkIntent,
  SparkWeekPreviewItem,
} from '@/core/data/spark/spark.interface';
import { localize } from '@/core/gambits';
import { haversineDistance } from '@/core/helpers/location.helper';
import { createLogger } from '@/core/helpers/logger.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { firestore } from '@/core/network/firebase';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useSparkIntentStore } from '@/stores/spark.intent';
import { useSparkProposalStore } from '@/stores/spark.proposal';
import { useSparkStore, type SparkGambitPatch, type SparkIntentResultsSnapshot } from '@/stores/spark.store';
import { useSparkWeekPreviewStore } from '@/stores/spark.weekPreview';
import {
  hasUnansweredSparkInvitation,
  sortSparkGambitsByAttendees,
  type SparkResultEntry,
} from '@/stores/utils/spark-results-priority';
import { getSparkOccupancyFingerprint } from '@/stores/utils/spark-unseen-changes';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import InitializingSpinner from '@/views/components/InitializingSpinner.vue';
import ScreenNav from '@/views/components/ScreenNav.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import CustomProposalCard from '@/views/main/sparks/cards/CustomCard.vue';
import DisabledCard from '@/views/main/sparks/cards/DisabledCard.vue';
import GambitCard from '@/views/main/sparks/cards/GambitCard.vue';
import NoLocationsCard from '@/views/main/sparks/cards/NoLocationsCard.vue';
import NotFoundCard from '@/views/main/sparks/cards/NotFoundCard.vue';
import SkeletonCard from '@/views/main/sparks/cards/SkeletonCard.vue';
import SparkCard from '@/views/main/sparks/cards/SparkCard.vue';
import SparkWeekPreview from '@/views/main/sparks/cards/SparkWeekPreview.vue';
import ScrollSnap, { ScrollAxis, ScrollSnapType } from '@/views/main/sparks/components/ScrollSnap.vue';
import SnapSlide from '@/views/main/sparks/components/SnapSlide.vue';
import SparkMatchCity from '@/views/main/sparks/components/SparkMatchCity.vue';
import ChooseAvailability from '@/views/main/sparks/modals/ChooseAvailability.vue';
import ConfirmDialog from '@/views/main/sparks/modals/ConfirmDialog.vue';
import SuccessListDialog, { type SuccessListDialogItem } from '@/views/main/sparks/modals/SuccessListDialog.vue';
import { scrollTo } from '@/views/main/sparks/utils/scroll';
import { addDays, addHours, format, isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { doc, onSnapshot } from 'firebase/firestore';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import {
  findEnabledIntentByDate,
  findIntentByDate,
  getEarliestEnabledIntent,
  getEnabledIntentsSorted,
} from './utils/intent-route';
import {
  getDefaultResultsDateKey,
  getResultsReferenceTimezone,
  getTodayDateKey,
  RESULTS_CUTOFF_HOUR,
} from './utils/intent-visibility';
import { getLiveSuggestedProposalTime } from './utils/seeded-suggestion-time';
import { getGambitVisibleUntil, getProposalVisibleUntil, getSuggestionVisibleUntil } from './utils/spark-visibility';

const MIN_START_HOUR = 8;
const MAX_END_HOUR = 22;
const DEFAULT_FALLBACK_START_HOUR = 8;
const DEFAULT_FALLBACK_END_HOUR = 22;
const DEFAULT_FALLBACK_RADIUS_METERS = 2500;
const DEFAULT_FALLBACK_MAP_ZOOM = 13;
const RESULT_VISIBLE_DAY_COUNT = 7;
const UNSEEN_TOC_POLL_MS = 12000;

const logger = createLogger('SparkIgnition');

const slideClasses =
  'slide spark-card-slide result-slide match-card-slide slide spark-card-slide result-slide match-card-slide';

const WEEK_TOC_DATE = '__week__';

const liveSnapIndex = ref<number>(0);
const isScrollFrozen = ref<boolean>(false);
const showNewResultsAbove = ref<boolean>(false);
const firstChangedCardToken = ref<string | null>(null);

// GLOBAL STORES

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const cityStore = useCityStore();
const { city, isInitialized: isCityInitialized, hasSparkableLocations } = storeToRefs(cityStore);

const geolocationStore = useGeolocationStore();

const sparkStore = useSparkStore();
const {
  intents,
  intentListItems,
  intentResults,
  invitedProposals,
  invitedChallenges,
  invitedConfirmedGambits,
  confirmedGambits,
  sparkResults,
  gambitResults,
  activeResultsIntentId,
  chosenLocationIds,
  isLoading,
  isLoadingIntents,
  isLoadingIntentResults,
  isLoadingGambitResults,
  isActiveIntentMissingLocations,
  myGambits,
  isIntentLoading,
} = storeToRefs(sparkStore);

const weekPreviewStore = useSparkWeekPreviewStore();
const { preview: weekPreview, isLoading: isWeekPreviewLoading } = storeToRefs(weekPreviewStore);

const showNoLocationsCard = computed(() => {
  return (
    isCityInitialized.value &&
    !!userDetails.value?.id &&
    (!hasSparkableLocations.value || isActiveIntentMissingLocations.value)
  );
});

const showSparkContent = computed(() => {
  return !!userDetails.value?.id && hasSparkableLocations.value && !isActiveIntentMissingLocations.value;
});

const showEmptyReloadCard = computed(() => {
  return (
    isCityInitialized.value &&
    !!userDetails.value?.id &&
    hasSparkableLocations.value &&
    !isActiveIntentMissingLocations.value &&
    !sparkIntent.value &&
    !intents.value.length &&
    !isLoadingIntents.value &&
    !isIntentLoading.value &&
    !isFetchingResultsRouteInFlight.value &&
    !isDiscoveryBootstrapInFlight.value &&
    !isInitializing.value
  );
});

const GPS_FAR_FROM_CITY_M = 20_000;

const sparkCityLabel = computed((): string | null => {
  const cityName = city.value?.name;
  if (!cityName) return null;

  const gpsLocation = geolocationStore.userLocation?.location;
  const cityCoords = city.value?.coordinates;

  if (gpsLocation && cityCoords) {
    const distance = haversineDistance(gpsLocation, cityCoords);
    if (distance > GPS_FAR_FROM_CITY_M) {
      return `${localize('spark.near_you')} + ${cityName}`;
    }
  }

  return null;
});

const weekPreviewAreaLabel = computed((): string | null => sparkCityLabel.value || city.value?.name || null);

const sparkCityLabelRef = ref<HTMLElement | null>(null);
const isCitySearchOpen = ref(false);
const citySearchResetKey = ref(0);

const toggleCitySearch = () => {
  isCitySearchOpen.value = !isCitySearchOpen.value;
};

const handleSparkCitySelected = async (item: GamDropdownItem | null) => {
  if (!item?.coordinates || item?.id === city.value?.googlePlaceId) {
    return;
  }

  isCitySearchOpen.value = false;
  citySearchResetKey.value++;
  isCitySwitchLoading.value = true;

  try {
    await cityStore.determineCity(item.coordinates);

    clearLoadedResultDays();
    intentStore.clearIntentStore();
    chosenLocationIds.value = [];

    await bootstrapDiscoveryData();

    const fallbackIntent = enabledIntentsByDate.value[0];
    if (fallbackIntent) {
      await activateIntent(fallbackIntent);
    } else {
      await redirectToFallbackResults(defaultResultsDateKey.value);
    }
  } finally {
    isCitySwitchLoading.value = false;
  }
};

const handleCitySearchClickOutside = (event: MouseEvent) => {
  if (!isCitySearchOpen.value) return;
  if (sparkCityLabelRef.value && !sparkCityLabelRef.value.contains(event.target as Node)) {
    isCitySearchOpen.value = false;
  }
};

watch(isCitySearchOpen, (open) => {
  if (open) {
    document.addEventListener('pointerdown', handleCitySearchClickOutside, true);
  } else {
    document.removeEventListener('pointerdown', handleCitySearchClickOutside, true);
  }
});

const isReloadingSparks = ref(false);

async function handleReloadSparks() {
  isReloadingSparks.value = true;
  try {
    await authStore.fetchUserInfo(true);
    await sparkStore.ensureDailyAvailabilitiesExtended(city.value?.id);
    await sparkStore.fetchIntents();
    await sparkStore.ensureIntentsForInvitationDates();

    if (!sparkIntent.value && intents.value.length) {
      const firstIntent = earliestEnabledIntent.value || intents.value[0];
      if (firstIntent) {
        await activateIntent(firstIntent);
      }
    }
  } finally {
    isReloadingSparks.value = false;
  }
}

const navigationStore = useNavigationStore();
const { refreshResultsSignal, gotoNewProposalSignal, openAvailabilitiesSignal } = storeToRefs(navigationStore);

const intentStore = useSparkIntentStore();
const { sparkIntent } = storeToRefs(intentStore);

const proposalStore = useSparkProposalStore();
const { sparkProposal } = storeToRefs(proposalStore);

const router = useRouter();
const route = useRoute();

const goToProfile = () => {
  const username = authStore.userDetails?.username;
  if (username) {
    router.push({ name: RouterNameType.USERNAME, params: { username }, query: { back: 'history' } });
  }
};

const goToMap = () => {
  router.push({ name: RouterNameType.LOCATIONS });
};

// State

enum SparkStep {
  WIZARD = 'wizard',
  RESULTS = 'results',
}

const sparkStep = ref<SparkStep>(SparkStep.WIZARD);

const timeSpanKey = ref<string>('');

const startHour = ref<number | null>(MIN_START_HOUR + 2);
const endHour = ref<number | null>(MAX_END_HOUR - 2);

const currentScreen = ref<SparkScreen>(SparkScreen.DAYS);
const currentProposalId = ref<string | null>(null);
const currentGambitId = ref<string | null>(null);
const isExactProposal = ref<boolean>(false);

const isInitializing = ref<boolean>(true);
const isKeepAliveActive = ref<boolean>(true);
let lastActiveCityId: string | null = null;
const hasLeftSparks = () => !isKeepAliveActive.value || route.name !== RouterNameType.SPARKS;
const isScrollLocked = ref<boolean>(false);

const isEditAllowed = ref<boolean>(false);
const isPlacesAllowed = ref<boolean>(false);

const isCitySwitchLoading = ref<boolean>(false);
const isJoiningProposal = ref<boolean>(false);
let suppressResultDaySync = false;
const heldVoteProposalId = ref<string | null>(null);
const hasStaleTocResults = ref(false);
let ignoreUnseenTocColor = false;
let isApplyingAvailabilityResults = false;

const noteSelfTocAction = () => {
  ignoreUnseenTocColor = true;
};

const dayProposalUnsubscribes = new Map<string, () => void>();
const dayGambitUnsubscribes = new Map<string, () => void>();
const inFlightGambitDeckRefreshIntentIds = new Set<string>();
let unseenTocPollTimer: ReturnType<typeof setInterval> | null = null;

const stopUnseenTocPoll = () => {
  if (unseenTocPollTimer) {
    clearInterval(unseenTocPollTimer);
    unseenTocPollTimer = null;
  }
};

const teardownDayProposalListeners = () => {
  dayProposalUnsubscribes.forEach((unsubscribe) => unsubscribe());
  dayProposalUnsubscribes.clear();
};

const teardownDayGambitListeners = () => {
  dayGambitUnsubscribes.forEach((unsubscribe) => unsubscribe());
  dayGambitUnsubscribes.clear();
};

const teardownUnseenTocWatchers = () => {
  teardownDayProposalListeners();
  teardownDayGambitListeners();
  stopUnseenTocPoll();
};
const unreadSuccessItems = ref<SuccessListDialogItem[]>([]);
const isUnreadSuccessProcessing = ref<boolean>(false);
const dismissedSuccessProposalIds = new Set<string>();
const missingProposalState = ref<MissingProposalState | null>(null);
const loadedResultDays = ref<Array<LoadedResultDay>>([]);
const isLoadingNextResultDay = ref<boolean>(false);
const isLoadingPreviousResultDay = ref<boolean>(false);
const previousResultDayError = ref<string | null>(null);
const isDiscoveryBootstrapInFlight = ref<boolean>(false);
let discoveryBootstrapPromise: Promise<void> | null = null;
const isRefreshResultsInFlight = ref<boolean>(false);
const inFlightActiveResultsRefreshIntentIds = new Set<string>();
const proposalBootstrapInFlightKey = ref<string | null>(null);
const activeSnapIndex = ref<number>(0);
const isProgrammaticResultsScroll = ref<boolean>(false);
const lastResultsScrollBehavior = ref<string | null>(null);
/* Session-only: show until the user has snapped twice; comes back next visit. */
const SWIPE_HINT_SESSION_KEY = 'sparks.swipe_hint.snaps';
const SWIPE_HINT_DISMISS_AFTER = 2;
const swipeHintSnapCount = ref(Number(sessionStorage.getItem(SWIPE_HINT_SESSION_KEY) || '0') || 0);
const isFetchingResultsRouteInFlight = ref<boolean>(false);
const pendingFetchingResultsRoute = ref<{ date: string; source: FetchingResultsSource } | null>(null);
const queuedFetchingResultsRoute = ref<{ date: string; source: FetchingResultsSource } | null>(null);
let resultsScrollGuardTimeout: ReturnType<typeof setTimeout> | null = null;

type SparkFilteredResult = { segment: SegmentResult; proposal?: SegmentProposal };
type ResultCardKind =
  | 'prev_loader'
  | 'next_loader'
  | 'loading'
  | 'disabled'
  | 'gambit'
  | 'match'
  | 'proposal'
  | 'proposal_missing'
  | 'new'
  | 'end'
  | 'week';
type ResultsScrollBehavior = 'instant' | 'smooth';
type UnavailableReason = 'day';
type ProposalRoutePhase = 'result_reloading' | 'missing';
type FetchingResultsSource = 'toc' | 'prev_boundary' | 'next_boundary';

interface CustomProposalCreateResult {
  error?: {
    message: string;
  };
  proposalId?: string;
}

interface ResultCard {
  key: string;
  token: string;
  kind: ResultCardKind;
  date: string;
  expiresAt?: Date | string;
  intent: SparkIntent | null;
  dayBatchIndex?: number;
  gambitIndex?: number;
  resultIndex?: number;
  missingProposalId?: string;
  gambit?: GambitSearchResultDto;
  result?: SparkFilteredResult;
  classList: string[];
  domId: string;
}

/* The week card spans every day, so it never counts as content of one day. */
const isWeekCard = (card?: ResultCard | null): boolean => card?.kind === 'week';

interface MissingProposalState {
  proposalId: string;
  intentId: string | null;
  date: string;
  phase: ProposalRoutePhase;
  reloadError: string | null;
}

interface LoadedResultDay {
  intent: SparkIntent;
  isDisabled: boolean;
  unavailableReason: UnavailableReason | null;
  invitedChallenges: GambitSearchResultDto[];
  invitedConfirmedGambits: GambitSearchResultDto[];
  confirmedGambits: GambitSearchResultDto[];
  filteredResults: SparkFilteredResult[];
  invitedProposalIds: Set<string>;
  sourceSnapshot: SparkIntentResultsSnapshot | null;
}

interface ResultDayTocEntry {
  date: string;
  label: string;
  dayNumber: string;
  isDisabled: boolean;
  isSpark?: boolean;
}

const resolveIntentUpdatedAtTimestamp = (intent: SparkIntent): number => {
  const timestamp = Date.parse(intent.updatedAt);

  if (Number.isFinite(timestamp)) {
    return timestamp;
  }

  return 0;
};

const sortIntentCandidatesForDateSelection = (left: SparkIntent, right: SparkIntent): number => {
  const leftDisabledRank = left.isDisabled ? 1 : 0;
  const rightDisabledRank = right.isDisabled ? 1 : 0;

  if (leftDisabledRank !== rightDisabledRank) {
    return leftDisabledRank - rightDisabledRank;
  }

  const leftUpdatedAt = resolveIntentUpdatedAtTimestamp(left);
  const rightUpdatedAt = resolveIntentUpdatedAtTimestamp(right);

  if (leftUpdatedAt !== rightUpdatedAt) {
    return rightUpdatedAt - leftUpdatedAt;
  }

  return left.id.localeCompare(right.id);
};

const getIntentsForDate = (date: string): SparkIntent[] => {
  return intents.value.filter((intent) => intent.date === date);
};

const pickPreferredIntentForDate = (date: string): SparkIntent | null => {
  const intentsForDate = getIntentsForDate(date);

  if (!intentsForDate.length) {
    return null;
  }

  return [...intentsForDate].sort(sortIntentCandidatesForDateSelection)[0];
};

const resolveResultOverlapWindow = (entry: SparkResultEntry): { startAt: number; endAt: number } | null => {
  if (entry.proposal) {
    const proposalStartAt = Date.parse(entry.proposal.start);
    const proposalEndAt = Date.parse(entry.proposal.end);

    if (Number.isFinite(proposalStartAt) && Number.isFinite(proposalEndAt)) {
      return {
        startAt: proposalStartAt,
        endAt: proposalEndAt,
      };
    }
  }

  const segmentStartAt = Date.parse(entry.segment.start);
  const segmentEndAt = Date.parse(entry.segment.end);

  if (entry.proposal || !entry.segment.location?.id) {
    if (Number.isFinite(segmentStartAt) && Number.isFinite(segmentEndAt)) {
      return {
        startAt: segmentStartAt,
        endAt: segmentEndAt,
      };
    }

    return null;
  }

  const suggestedTime = getLiveSuggestedProposalTime(entry.segment, nowTime.value);
  const suggestedStartAt = suggestedTime.start.getTime();
  const suggestedEndAt = suggestedTime.end.getTime();

  if (Number.isFinite(suggestedStartAt) && Number.isFinite(suggestedEndAt)) {
    return {
      startAt: suggestedStartAt,
      endAt: suggestedEndAt,
    };
  }

  if (Number.isFinite(segmentStartAt) && Number.isFinite(segmentEndAt)) {
    return {
      startAt: segmentStartAt,
      endAt: segmentEndAt,
    };
  }

  return null;
};

const doesNotOverlap = (gambits: GambitSearchResultDto[], entry: SparkResultEntry) => {
  const resultWindow = resolveResultOverlapWindow(entry);

  if (!resultWindow) {
    return true;
  }

  return !gambits
    .filter((gambit) => gambit.isAttending === true)
    .some((gambit) => {
      const gambitStart = new Date(gambit.start).getTime();
      const gambitEnd = new Date(gambit.end).getTime();

      return resultWindow.startAt < gambitEnd && resultWindow.endAt > gambitStart;
    });
};

const hasGambitAtSameLocation = (locationGambits: GambitSearchResultDto[], entry: SparkResultEntry): boolean => {
  const resultWindow = resolveResultOverlapWindow(entry);
  if (!resultWindow) return false;

  const locationId = entry.segment.location?.id;
  if (!locationId) return false;

  return locationGambits.some((gambit) => {
    if (gambit.locationId !== locationId) return false;
    const gambitStart = new Date(gambit.start).getTime();
    const gambitEnd = new Date(gambit.end).getTime();
    return resultWindow.startAt < gambitEnd && resultWindow.endAt > gambitStart;
  });
};

const filterVisibleSparkResults = (
  results: SparkResultEntry[],
  gambits: GambitSearchResultDto[],
  locationGambits: GambitSearchResultDto[] = [],
): SparkFilteredResult[] => {
  return results
    .filter(
      (entry) => (entry.segment.matchCount > 1 || entry.proposal) && (entry.proposal || doesNotOverlap(gambits, entry)),
    )
    .filter((entry) => !entry.proposal?.isSuccessful)
    .filter((entry) => entry.proposal || !hasGambitAtSameLocation(locationGambits, entry))
    .map((entry) => ({
      segment: entry.segment,
      proposal: entry.proposal,
    }));
};

/* Both sides of a staleness check go through here, reading the raw payload rather
   than the rendered deck. Comparing a locally patched deck against a fresh filter
   pass used to drift on its own, because the two sides were filtered with
   different `attendedGambits` and merged with different priority-proposal state.
   Callers must fingerprint both snapshots in the same tick so the reachability
   test sees identical inputs. */
const getSnapshotOccupancyFingerprint = (
  snapshot: SparkIntentResultsSnapshot,
  excludeProposalIds: Set<string>,
  excludeGambitId?: string | null,
): string => {
  return getSparkOccupancyFingerprint(snapshot, {
    currentUserId: userDetails.value?.id,
    excludeProposalIds,
    excludeGambitId,
  });
};

const nowTime = ref<Date>(new Date());
let nowTimeout: number | null = null;

const handleNowTime = () => {
  /* Called from several restore paths; without this each call would start another minute chain. */
  nowTimeout && clearTimeout(nowTimeout);
  nowTime.value = new Date();
  nowTimeout = setTimeout(handleNowTime, (60 - nowTime.value.getSeconds()) * 1000) as any as number;
};

/* A suspended app may not run the pending minute tick for a while after it is reopened. */
const handleNowTimeOnResume = () => {
  if (document.visibilityState === 'visible' && isKeepAliveActive.value) handleNowTime();
};

onMounted(() => {
  document.addEventListener('visibilitychange', handleNowTimeOnResume);
});

onBeforeUnmount(() => {
  nowTimeout && clearTimeout(nowTimeout);
  nowTimeout = null;
  document.removeEventListener('visibilitychange', handleNowTimeOnResume);
});

// Computed values

const attendedGambits = computed(() => myGambits.value.filter((g) => g.isAttending));

const filteredResults = computed((): Array<{ segment: SegmentResult; proposal?: SegmentProposal }> => {
  return filterVisibleSparkResults(sparkResults.value, attendedGambits.value, confirmedGambits.value);
});

const enabledIntentsByDate = computed(() => {
  return getEnabledIntentsSorted(intents.value);
});

const intentsByDate = computed(() => {
  return [...intents.value].sort((a, b) => a.date.localeCompare(b.date));
});

const earliestEnabledIntent = computed(() => {
  return getEarliestEnabledIntent(intents.value);
});

const resultsReferenceTimezone = computed(() => {
  return getResultsReferenceTimezone({
    activeIntent: sparkIntent.value,
    earliestEnabledIntent: earliestEnabledIntent.value,
    city: city.value,
    browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
});

const minVisibleResultsDateKey = computed(() => {
  return getTodayDateKey(nowTime.value, resultsReferenceTimezone.value);
});

const defaultResultsDateKey = computed(() => {
  return getDefaultResultsDateKey(nowTime.value, resultsReferenceTimezone.value, RESULTS_CUTOFF_HOUR);
});

const parseDateKeyToLocalDate = (dateKey: string): Date => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const maxVisibleResultsDateKey = computed(() => {
  const firstVisibleDate = parseDateKeyToLocalDate(minVisibleResultsDateKey.value);
  return format(addDays(firstVisibleDate, RESULT_VISIBLE_DAY_COUNT - 1), 'yyyy-MM-dd');
});

const isDateWithinVisibleResultsWindow = (date: string): boolean => {
  return date >= minVisibleResultsDateKey.value && date <= maxVisibleResultsDateKey.value;
};

const clampResultsDateToVisibleWindow = (date: string): string => {
  if (date < minVisibleResultsDateKey.value) {
    return minVisibleResultsDateKey.value;
  }

  if (date > maxVisibleResultsDateKey.value) {
    return maxVisibleResultsDateKey.value;
  }

  return date;
};

const getPreviousEligibleIntents = (anchorDate: string): SparkIntent[] => {
  return intentsByDate.value.filter(
    (intent) => intent.date >= minVisibleResultsDateKey.value && intent.date < anchorDate,
  );
};

const nextResultIntent = computed((): SparkIntent | null => {
  const anchorDate = loadedResultDays.value[loadedResultDays.value.length - 1]?.intent.date || sparkIntent.value?.date;

  if (!anchorDate) {
    return null;
  }

  return (
    intentsByDate.value.find((intent) => intent.date > anchorDate && intent.date <= maxVisibleResultsDateKey.value) ||
    null
  );
});

const previousResultIntent = computed((): SparkIntent | null => {
  const anchorDate = loadedResultDays.value[0]?.intent.date || sparkIntent.value?.date;

  if (!anchorDate) {
    return null;
  }

  const eligibleIntents = getPreviousEligibleIntents(anchorDate);

  return eligibleIntents[eligibleIntents.length - 1] || null;
});

const renderPrevDayCard = computed((): boolean => {
  return Boolean(previousResultIntent.value);
});

const renderNextDayCard = computed((): boolean => {
  return Boolean(nextResultIntent.value);
});

const resultDayTocEntries = computed((): ResultDayTocEntry[] => {
  const activeResultDate =
    activeResultCard.value?.date || (typeof route.query.date === 'string' ? route.query.date : sparkIntent.value?.date);
  const firstVisibleDate = parseDateKeyToLocalDate(minVisibleResultsDateKey.value);

  return Array.from({ length: RESULT_VISIBLE_DAY_COUNT }, (_, offset) => {
    const date = addDays(firstVisibleDate, offset);
    const dateKey = format(date, 'yyyy-MM-dd');
    const preferredIntentForDate = pickPreferredIntentForDate(dateKey);

    return {
      date: dateKey,
      label: formatResultDayTocLabel(date),
      dayNumber: format(date, 'd'),
      isDisabled: !!preferredIntentForDate?.isDisabled,
    };
  });
});

const showResultDayToc = computed(() => {
  return resultDayTocEntries.value.length > 0;
});

const isResultPayloadLoading = computed(() => {
  return isLoadingIntentResults.value || isLoadingGambitResults.value;
});

const shouldShowResultDayLoadingPlaceholder = (resultDay: LoadedResultDay): boolean => {
  if (resultDay.isDisabled || resultDay.unavailableReason) {
    return false;
  }

  if (!sparkIntent.value || sparkIntent.value.id !== resultDay.intent.id) {
    return false;
  }

  if (!isResultPayloadLoading.value) {
    return false;
  }

  const { filteredResults, invitedChallenges, invitedConfirmedGambits, confirmedGambits } = resultDay;

  return (
    !invitedChallenges.length &&
    !invitedConfirmedGambits.length &&
    !confirmedGambits.length &&
    filteredResults.length === 0
  );
};

const chosenDate = computed((): string | undefined => {
  if (typeof route.query.date === 'undefined') {
    return undefined;
  }

  return String(route.query.date);
});

const dateQueryRegex = /^(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/;

const isValidDateString = (dateString: string) => {
  return dateQueryRegex.test(dateString);
};

const getProposalCardToken = (proposalId: string): string => {
  return `proposal:${proposalId}`;
};

const getGambitCardToken = (gambitId: string): string => {
  return `gambit:${gambitId}`;
};

const getMatchCardToken = (segment: { location: { id: string }; start: string }): string => {
  return `match:${segment.location.id}:${segment.start}`;
};

const parseLegacyMatchIndexToken = (token: string): number | null => {
  if (!token.startsWith('match:')) return null;
  const raw = token.slice('match:'.length);
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

const normalizeRouteViewToken = (rawToken?: string | null): string | null => {
  if (!rawToken || rawToken.trim().length === 0) {
    return null;
  }

  if (
    rawToken === 'loading' ||
    rawToken === 'fetching' ||
    rawToken === 'disabled' ||
    rawToken === 'new' ||
    rawToken === 'end'
  ) {
    return rawToken;
  }

  if (rawToken.startsWith('proposal:') || rawToken.startsWith('gambit:') || rawToken.startsWith('match:')) {
    return rawToken;
  }

  return null;
};

const activeRouteViewToken = computed<string | null>(() => {
  const routeView = typeof route.query.view === 'string' ? route.query.view : undefined;
  return normalizeRouteViewToken(routeView);
});

const clearMissingProposalState = (): void => {
  missingProposalState.value = null;
};

const upsertMissingProposalState = (params: {
  proposalId: string;
  intentId: string | null;
  date: string;
  phase: ProposalRoutePhase;
  reloadError?: string | null;
}): void => {
  const previousState = missingProposalState.value;
  const isSameProposal = previousState?.proposalId === params.proposalId && previousState.date === params.date;

  missingProposalState.value = {
    proposalId: params.proposalId,
    intentId: params.intentId,
    date: params.date,
    phase: params.phase,
    reloadError:
      params.reloadError ?? (isSameProposal && params.phase === 'missing' ? previousState.reloadError : null),
  };
};

const isLinkedProposalPresentInResults = (proposalId: string): boolean => {
  return sparkResults.value.some((entry) => entry.proposal?.id === proposalId);
};

const buildProposalBootstrapKey = (date: string, proposalId: string): string => {
  return `${date}::${proposalId}`;
};

const holdVotedCardInPlace = (proposalId: string) => {
  heldVoteProposalId.value = proposalId;
  suppressResultDaySync = true;
};

const clearVotedCardHold = () => {
  heldVoteProposalId.value = null;
  suppressResultDaySync = false;
};

const replaceProposalInSnapshot = (
  snapshot: SparkIntentResultsSnapshot,
  proposal: SegmentProposal,
): SparkIntentResultsSnapshot => {
  return {
    ...snapshot,
    intentResults: snapshot.intentResults.map((segment) =>
      (segment.proposals || []).some((entry) => entry.id === proposal.id)
        ? { ...segment, proposals: segment.proposals.map((entry) => (entry.id === proposal.id ? proposal : entry)) }
        : segment,
    ),
    invitedProposals: snapshot.invitedProposals.map((entry) => (entry.id === proposal.id ? proposal : entry)),
    sparkResults: snapshot.sparkResults.map((entry) =>
      entry.proposal?.id === proposal.id ? { ...entry, proposal } : entry,
    ),
  };
};

const collectLoadedDayGambitIds = (resultDay: LoadedResultDay | null | undefined): string[] => {
  if (!resultDay) {
    return [];
  }

  return [...resultDay.invitedChallenges, ...resultDay.invitedConfirmedGambits, ...resultDay.confirmedGambits].map(
    (gambit) => gambit.id,
  );
};

const omitGambitFromDay = (day: LoadedResultDay, gambitId: string): LoadedResultDay => {
  const invitedChallenges = day.invitedChallenges.filter((gambit) => gambit.id !== gambitId);
  const invitedConfirmedGambits = day.invitedConfirmedGambits.filter((gambit) => gambit.id !== gambitId);
  const confirmedGambits = day.confirmedGambits.filter((gambit) => gambit.id !== gambitId);

  return {
    ...day,
    invitedChallenges,
    invitedConfirmedGambits,
    confirmedGambits,
    sourceSnapshot: day.sourceSnapshot
      ? {
          ...day.sourceSnapshot,
          invitedChallenges: day.sourceSnapshot.invitedChallenges.filter((gambit) => gambit.id !== gambitId),
          invitedConfirmedGambits: day.sourceSnapshot.invitedConfirmedGambits.filter(
            (gambit) => gambit.id !== gambitId,
          ),
          confirmedGambits: day.sourceSnapshot.confirmedGambits.filter((gambit) => gambit.id !== gambitId),
        }
      : null,
  };
};

const patchGambitInDay = (day: LoadedResultDay, gambitId: string, patch: SparkGambitPatch): LoadedResultDay => {
  if (!collectLoadedDayGambitIds(day).includes(gambitId)) {
    return day;
  }

  const patchList = (gambits: GambitSearchResultDto[]) =>
    gambits.map((gambit) => (gambit.id === gambitId ? patch(gambit) : gambit));

  return {
    ...day,
    invitedChallenges: patchList(day.invitedChallenges),
    invitedConfirmedGambits: patchList(day.invitedConfirmedGambits),
    confirmedGambits: patchList(day.confirmedGambits),
    sourceSnapshot: day.sourceSnapshot
      ? {
          ...day.sourceSnapshot,
          invitedChallenges: patchList(day.sourceSnapshot.invitedChallenges),
          invitedConfirmedGambits: patchList(day.sourceSnapshot.invitedConfirmedGambits),
          confirmedGambits: patchList(day.sourceSnapshot.confirmedGambits),
        }
      : null,
  };
};

const clampSnapIndexesToDeck = () => {
  const lastIndex = Math.max(resultDeck.value.length - 1, 0);

  if (activeSnapIndex.value > lastIndex) {
    activeSnapIndex.value = lastIndex;
  }

  if (liveSnapIndex.value > lastIndex) {
    liveSnapIndex.value = lastIndex;
  }
};

/** Keep the tape on the same card when a live drop removes slides ahead of the camera. */
const preserveSnapIndexAfterRemovedToken = (token: string): number => {
  const removedIndexes = resultDeck.value
    .map((card, index) => (card.token === token ? index : -1))
    .filter((index) => index >= 0);

  if (!removedIndexes.length) {
    return 0;
  }

  const aheadCount = removedIndexes.filter((index) => index < activeSnapIndex.value).length;
  if (aheadCount) {
    activeSnapIndex.value -= aheadCount;
  }

  const liveAheadCount = removedIndexes.filter((index) => index < liveSnapIndex.value).length;
  if (liveAheadCount) {
    liveSnapIndex.value -= liveAheadCount;
  }

  return aheadCount;
};

const compensateResultsScrollAfterAheadDrop = (aheadCount: number) => {
  if (aheadCount <= 0) {
    return;
  }

  const container = getResultsContainer();
  if (!container) {
    return;
  }

  const slideHeight = container.clientHeight;
  if (slideHeight <= 0) {
    return;
  }

  container.scrollTop = Math.max(0, container.scrollTop - aheadCount * slideHeight);
};

const removeGambitFromLoadedDays = (gambitId: string) => {
  const aheadCount = preserveSnapIndexAfterRemovedToken(getGambitCardToken(gambitId));
  loadedResultDays.value = loadedResultDays.value.map((day) => omitGambitFromDay(day, gambitId));
  clampSnapIndexesToDeck();

  if (aheadCount) {
    void nextTick(() => {
      compensateResultsScrollAfterAheadDrop(aheadCount);
    });
  }
};

const omitProposalFromDay = (day: LoadedResultDay, proposalId: string): LoadedResultDay => {
  const filteredResults = day.filteredResults.filter((entry) => entry.proposal?.id !== proposalId);

  return {
    ...day,
    filteredResults,
    sourceSnapshot: day.sourceSnapshot
      ? {
          ...day.sourceSnapshot,
          invitedProposals: day.sourceSnapshot.invitedProposals.filter((proposal) => proposal.id !== proposalId),
          intentResults: day.sourceSnapshot.intentResults.map((segment) => ({
            ...segment,
            proposals: (segment.proposals || []).filter((proposal) => proposal.id !== proposalId),
          })),
          sparkResults: day.sourceSnapshot.sparkResults.filter((entry) => entry.proposal?.id !== proposalId),
        }
      : null,
  };
};

const removeProposalFromLoadedDays = (proposalId: string) => {
  const aheadCount = preserveSnapIndexAfterRemovedToken(getProposalCardToken(proposalId));
  loadedResultDays.value = loadedResultDays.value.map((day) => omitProposalFromDay(day, proposalId));
  clampSnapIndexesToDeck();

  if (aheadCount) {
    void nextTick(() => {
      compensateResultsScrollAfterAheadDrop(aheadCount);
    });
  }

  if (missingProposalState.value?.proposalId === proposalId) {
    clearMissingProposalState();
  }

  if (heldVoteProposalId.value === proposalId) {
    clearVotedCardHold();
  }
};

const isViewingProposal = (proposalId: string): boolean => {
  const viewToken = typeof route.query.view === 'string' ? route.query.view : '';
  return viewToken === getProposalCardToken(proposalId) || activeResultCard.value?.result?.proposal?.id === proposalId;
};

const isViewingGambit = (gambitId: string): boolean => {
  const viewToken = typeof route.query.view === 'string' ? route.query.view : '';
  return viewToken === getGambitCardToken(gambitId) || activeResultCard.value?.gambit?.id === gambitId;
};

const handleMergedProposal = (proposalId: string, gambitId: string) => {
  removeProposalFromLoadedDays(proposalId);

  if (!isKeepAliveActive.value) {
    return;
  }

  const day =
    findLoadedDayForGambit(gambitId) ||
    findLoadedResultDayByIntentId(activeResultsIntentId.value || '') ||
    loadedResultDays.value[0];

  if (!day) {
    return;
  }

  if (isViewingProposal(proposalId)) {
    void handleResumeIntentGambit(day.intent, gambitId);
    return;
  }

  void refreshLoadedDayGambitCards(day.intent);
};

const applyGambitListsToLoadedDay = (intentId: string, snapshot: SparkIntentResultsSnapshot) => {
  loadedResultDays.value = loadedResultDays.value.map((day) => {
    if (day.intent.id !== intentId) {
      return day;
    }

    return {
      ...day,
      invitedChallenges: sortSparkGambitsByAttendees(snapshot.invitedChallenges),
      invitedConfirmedGambits: sortSparkGambitsByAttendees(snapshot.invitedConfirmedGambits),
      confirmedGambits: sortSparkGambitsByAttendees(snapshot.confirmedGambits),
      sourceSnapshot: day.sourceSnapshot
        ? {
            ...day.sourceSnapshot,
            invitedChallenges: [...snapshot.invitedChallenges],
            invitedConfirmedGambits: [...snapshot.invitedConfirmedGambits],
            confirmedGambits: [...snapshot.confirmedGambits],
          }
        : null,
    };
  });

  if (activeResultsIntentId.value === intentId) {
    sparkStore.replaceGambitLists(snapshot);
  }
};

const refreshLoadedDayGambitCards = async (intent: SparkIntent) => {
  if (inFlightGambitDeckRefreshIntentIds.has(intent.id)) {
    return;
  }

  inFlightGambitDeckRefreshIntentIds.add(intent.id);

  try {
    const snapshot = await sparkStore.fetchIntentResultsSnapshot(intent, {
      forceRefresh: true,
      silent: true,
    });
    applyGambitListsToLoadedDay(intent.id, snapshot);
  } catch (error) {
    logger.warn('Failed to refresh Spark gambit cards', {
      intentId: intent.id,
      intentDate: intent.date,
      error,
    });
  } finally {
    inFlightGambitDeckRefreshIntentIds.delete(intent.id);
  }
};

const findLoadedDayForGambit = (gambitId: string): LoadedResultDay | undefined => {
  return loadedResultDays.value.find((day) => collectLoadedDayGambitIds(day).includes(gambitId));
};

const handleGambitAttendanceChanged = () => {
  void sparkStore.findMyGambits(new Date(), addDays(new Date(), 7));
};

const patchLoadedResultDayProposal = (proposal: SegmentProposal) => {
  loadedResultDays.value = loadedResultDays.value.map((day) => {
    const resultIndex = day.filteredResults.findIndex((entry) => entry.proposal?.id === proposal.id);

    if (resultIndex < 0) {
      return day;
    }

    const nextFilteredResults = [...day.filteredResults];
    nextFilteredResults[resultIndex] = {
      ...nextFilteredResults[resultIndex],
      proposal,
    };

    return {
      ...day,
      filteredResults: nextFilteredResults,
      /* The day's snapshot is what staleness checks treat as seen, so it carries
         the same update. Otherwise your own Meet Anyways looks like somebody
         else's news on the next poll. */
      sourceSnapshot: day.sourceSnapshot ? replaceProposalInSnapshot(day.sourceSnapshot, proposal) : null,
    };
  });
};

const getInvitationGambitsForDay = (resultDay: LoadedResultDay) => {
  return sortSparkGambitsByAttendees([...resultDay.invitedChallenges, ...resultDay.invitedConfirmedGambits]);
};

const resultDeck = computed((): ResultCard[] => {
  const cards: ResultCard[] = [
    {
      key: 'week-preview',
      token: 'week',
      kind: 'week',
      date: '',
      intent: null,
      classList: slideClasses.split(' '),
      domId: 'week-preview',
    },
  ];
  const prevIntent = renderPrevDayCard.value ? previousResultIntent.value : null;
  const nextIntent = renderNextDayCard.value ? nextResultIntent.value : null;

  if (prevIntent) {
    cards.push({
      key: `prev-loader-${prevIntent.id}`,
      token: 'fetching',
      kind: 'prev_loader',
      date: prevIntent.date,
      intent: prevIntent,
      classList: slideClasses.split(' '),
      domId: 'prev-day',
    });
  }

  loadedResultDays.value.forEach((resultDay, dayBatchIndex) => {
    const { intent } = resultDay;

    if (resultDay.isDisabled || resultDay.unavailableReason) {
      resultDay.filteredResults
        .filter((result) => result.proposal && resultDay.invitedProposalIds.has(result.proposal.id))
        .forEach((result, resultIndex) => {
          const token = getProposalCardToken(result.proposal!.id);
          const expiresAt = getProposalVisibleUntil(result.proposal!.start ?? result.segment.start);

          isAfter(expiresAt, nowTime.value) &&
            cards.push({
              key: `result-${intent.id}-proposal-${result.proposal!.id}`,
              token,
              kind: 'proposal',
              date: intent.date,
              expiresAt,
              intent,
              dayBatchIndex,
              resultIndex,
              result,
              classList: ['slide', 'spark-card-slide', 'result-slide', 'match-card-slide'],
              domId: `proposal-${intent.id}-${result.proposal!.id}`,
            });
        });

      getInvitationGambitsForDay(resultDay).forEach((gambit, gambitIndex) => {
        const expiresAt = getGambitVisibleUntil(gambit);
        isAfter(expiresAt, nowTime.value) &&
          cards.push({
            key: `gambit-${intent.id}-${gambit.id}`,
            token: getGambitCardToken(gambit.id),
            kind: 'gambit',
            date: intent.date,
            expiresAt,
            intent,
            dayBatchIndex,
            gambitIndex,
            gambit,
            classList: [
              'slide',
              'spark-card-slide',
              'result-slide',
              'gambit-card-slide',
              `spark-gambit-${dayBatchIndex}-${gambitIndex}`,
            ],
            domId: `gambit-${intent.id}-${gambit.id}`,
          });
      });

      cards.push({
        key: `disabled-${intent.id}`,
        token: 'disabled',
        kind: 'disabled',
        date: intent.date,
        intent,
        dayBatchIndex,
        classList: ['slide', 'spark-card-slide', 'result-slide', 'loader-slide'],
        domId: `disabled-${intent.id}`,
      });

      return;
    }

    if (shouldShowResultDayLoadingPlaceholder(resultDay)) {
      cards.push({
        key: `loading-${intent.id}`,
        token: 'fetching',
        kind: 'loading',
        date: intent.date,
        intent,
        dayBatchIndex,
        classList: [
          'slide',
          'spark-card-slide',
          'result-slide',
          'day-placeholder-slide',
          'intent-results-loading-slide',
        ],
        domId: `intent-results-loading-${intent.id}`,
      });
    }

    const activeMissingProposal = missingProposalState.value;
    const isMissingProposalForIntent = Boolean(
      activeMissingProposal &&
        activeMissingProposal.intentId === intent.id &&
        activeMissingProposal.date === intent.date &&
        (activeMissingProposal.phase === 'missing' || activeMissingProposal.phase === 'result_reloading') &&
        !isLinkedProposalPresentInResults(activeMissingProposal.proposalId),
    );

    if (activeMissingProposal && isMissingProposalForIntent) {
      cards.push({
        key: `proposal-missing-${intent.id}-${activeMissingProposal.proposalId}`,
        token: getProposalCardToken(activeMissingProposal.proposalId),
        kind: 'proposal_missing',
        date: intent.date,
        intent,
        dayBatchIndex,
        missingProposalId: activeMissingProposal.proposalId,
        classList: ['slide', 'spark-card-slide', 'result-slide', 'day-placeholder-slide', 'match-card-slide'],
        domId: `proposal-missing-${intent.id}-${activeMissingProposal.proposalId}`,
      });
    }

    resultDay.filteredResults.forEach((result, resultIndex) => {
      if (!result.proposal || !resultDay.invitedProposalIds.has(result.proposal.id)) return;
      const token = getProposalCardToken(result.proposal.id);
      const expiresAt = getProposalVisibleUntil(result.proposal.start ?? result.segment.start);

      isAfter(expiresAt, nowTime.value) &&
        cards.push({
          key: `result-${intent.id}-proposal-${result.proposal.id}`,
          token,
          kind: 'proposal',
          date: intent.date,
          expiresAt,
          intent,
          dayBatchIndex,
          resultIndex,
          result,
          classList: ['slide', 'spark-card-slide', 'result-slide', 'match-card-slide'],
          domId: `proposal-${intent.id}-${result.proposal.id}`,
        });
    });

    getInvitationGambitsForDay(resultDay).forEach((gambit, gambitIndex) => {
      const expiresAt = getGambitVisibleUntil(gambit);
      isAfter(expiresAt, nowTime.value) &&
        cards.push({
          key: `gambit-${intent.id}-${gambit.id}`,
          token: getGambitCardToken(gambit.id),
          kind: 'gambit',
          date: intent.date,
          expiresAt,
          intent,
          dayBatchIndex,
          gambitIndex,
          gambit,
          classList: [
            'slide',
            'spark-card-slide',
            'result-slide',
            'gambit-card-slide',
            `spark-gambit-${dayBatchIndex}-${gambitIndex}`,
          ],
          domId: `gambit-${intent.id}-${gambit.id}`,
        });
    });

    resultDay.confirmedGambits.forEach((gambit, gambitIndex) => {
      const expiresAt = getGambitVisibleUntil(gambit);
      isAfter(expiresAt, nowTime.value) &&
        cards.push({
          key: `gambit-${intent.id}-${gambit.id}`,
          token: getGambitCardToken(gambit.id),
          kind: 'gambit',
          date: intent.date,
          expiresAt,
          intent,
          dayBatchIndex,
          gambitIndex,
          gambit,
          classList: [
            'slide',
            'spark-card-slide',
            'result-slide',
            'gambit-card-slide',
            `spark-gambit-${dayBatchIndex}-${gambitIndex}`,
          ],
          domId: `gambit-${intent.id}-${gambit.id}`,
        });
    });

    resultDay.filteredResults.forEach((result, resultIndex) => {
      if (result.proposal && resultDay.invitedProposalIds.has(result.proposal.id)) return;
      const matchSegmentKey = `${result.segment.location?.id || 'unknown'}-${result.segment.start}`;
      const token = result.proposal ? getProposalCardToken(result.proposal.id) : getMatchCardToken(result.segment);

      const expiresAt = result.proposal
        ? getProposalVisibleUntil(result.proposal.start ?? result.segment.start)
        : getSuggestionVisibleUntil(getLiveSuggestedProposalTime(result.segment, nowTime.value).start);

      isAfter(expiresAt, nowTime.value) &&
        cards.push({
          key: `result-${intent.id}-${result.proposal ? `proposal-${result.proposal.id}` : `match-${matchSegmentKey}`}`,
          token,
          kind: result.proposal ? 'proposal' : 'match',
          date: intent.date,
          expiresAt,
          intent,
          dayBatchIndex,
          resultIndex,
          result,
          classList: ['slide', 'spark-card-slide', 'result-slide', 'match-card-slide'],
          domId: result.proposal
            ? `proposal-${intent.id}-${result.proposal.id}`
            : `match-${intent.id}-${matchSegmentKey}`,
        });
    });

    if (shouldShowCustomCard(intent) && !shouldShowResultDayLoadingPlaceholder(resultDay)) {
      cards.push({
        key: `new-${intent.id}`,
        token: 'new',
        kind: 'new',
        date: intent.date,
        intent,
        dayBatchIndex,
        classList: ['slide', 'spark-card-slide', 'result-slide', 'custom-card-slide'],
        domId: `new-${intent.id}`,
      });
    }
  });

  if (nextIntent) {
    cards.push({
      key: `next-loader-${nextIntent.id}`,
      token: 'fetching',
      kind: 'next_loader',
      date: nextIntent.date,
      intent: nextIntent,
      classList: slideClasses.split(' '),
      domId: 'next-day',
    });
  }

  return cards;
});

const findDeckIndexForRouteCard = (date: string | undefined, token: string): number => {
  if (!resultDeck.value.length) {
    return -1;
  }

  if (token === 'fetching' && date) {
    const boundaryLoaderIndex = resultDeck.value.findIndex(
      (card) => (card.kind === 'prev_loader' || card.kind === 'next_loader') && card.date === date,
    );

    if (boundaryLoaderIndex >= 0) {
      return boundaryLoaderIndex;
    }
  }

  if (token.startsWith('proposal:') || token.startsWith('gambit:')) {
    return resultDeck.value.findIndex(
      (card) => !isWeekCard(card) && card.token === token && (!date || card.date === date),
    );
  }

  if (token.startsWith('match:')) {
    const directMatchIndex = resultDeck.value.findIndex((card) => card.token === token && card.date === date);

    if (directMatchIndex >= 0) {
      return directMatchIndex;
    }

    if (!date) {
      return -1;
    }

    const legacyIndex = parseLegacyMatchIndexToken(token);

    if (legacyIndex === null) {
      return -1;
    }

    return resultDeck.value.findIndex(
      (card) =>
        card.date === date && card.resultIndex === legacyIndex && (card.kind === 'match' || card.kind === 'proposal'),
    );
  }

  if (token === 'end') {
    return resultDeck.value.findIndex((card) => card.token === token);
  }

  return resultDeck.value.findIndex((card) => card.token === token && card.date === date);
};

const isDayContentCard = (card: ResultCard): boolean => {
  return (
    !isWeekCard(card) &&
    card.kind !== 'end' &&
    card.kind !== 'prev_loader' &&
    card.kind !== 'next_loader' &&
    card.kind !== 'loading' &&
    card.kind !== 'proposal_missing'
  );
};

const isSkeletonResultCard = (card?: ResultCard | null): boolean => {
  return !!card && (card.kind === 'loading' || card.kind === 'prev_loader' || card.kind === 'next_loader');
};

const findPrioritizedResultCardForDate = (date: string): ResultCard | null => {
  const dayCards = resultDeck.value.filter((card) => card.date === date && isDayContentCard(card));

  return (
    dayCards.find((card) => card.kind === 'proposal' || card.kind === 'gambit' || card.kind === 'match') ||
    dayCards.find((card) => card.kind === 'new') ||
    dayCards[0] ||
    null
  );
};

const findFallbackDeckIndex = (date: string | undefined): number => {
  if (!resultDeck.value.length) {
    return -1;
  }

  if (date) {
    const prioritizedCard = findPrioritizedResultCardForDate(date);

    if (prioritizedCard) {
      const prioritizedIndex = resultDeck.value.findIndex((card) => card.key === prioritizedCard.key);

      if (prioritizedIndex >= 0) {
        return prioritizedIndex;
      }
    }

    const sameDayFirst = resultDeck.value.findIndex((card) => card.date === date && isDayContentCard(card));

    if (sameDayFirst >= 0) {
      return sameDayFirst;
    }
  }

  const firstNonBoundaryLoader = resultDeck.value.findIndex(
    (card) => !isWeekCard(card) && card.kind !== 'prev_loader' && card.kind !== 'next_loader',
  );
  return firstNonBoundaryLoader >= 0 ? firstNonBoundaryLoader : 0;
};

const resolveFallbackCardTokenForDate = (date: string): string | null => {
  const prioritizedCard = findPrioritizedResultCardForDate(date);

  if (prioritizedCard) {
    return prioritizedCard.token;
  }

  const fallbackIndex = findFallbackDeckIndex(date);

  if (fallbackIndex < 0) {
    return null;
  }

  const fallbackToken = resultDeck.value[fallbackIndex]?.token || null;
  return fallbackToken === 'fetching' ? null : fallbackToken;
};

const getIntentResultDeckCards = (intentDate: string): ResultCard[] => {
  return resultDeck.value.filter(
    (card) =>
      !isWeekCard(card) &&
      card.date === intentDate &&
      card.kind !== 'prev_loader' &&
      card.kind !== 'next_loader' &&
      card.kind !== 'end',
  );
};

const resolveTransitionAnchorCardToken = (intent: SparkIntent, source: FetchingResultsSource): string | null => {
  const dayCards = getIntentResultDeckCards(intent.date);

  if (!dayCards.length) {
    return resolveFallbackCardTokenForDate(intent.date);
  }

  if (source === 'toc' || source === 'next_boundary') {
    return dayCards[0]?.token || null;
  }

  if (source === 'prev_boundary') {
    const previousDayAnchor = [...dayCards].reverse().find((card) => card.kind !== 'new');
    return previousDayAnchor?.token || dayCards[0]?.token || null;
  }

  return resolveFallbackCardTokenForDate(intent.date);
};

const activeResultCard = computed<ResultCard | null>(() => {
  return resultDeck.value[activeSnapIndex.value] || null;
});

const showSwipeHint = computed(() => {
  return (
    sparkStep.value === SparkStep.RESULTS &&
    showSparkContent.value &&
    !isCitySwitchLoading.value &&
    resultDeck.value.length > 1 &&
    swipeHintSnapCount.value < SWIPE_HINT_DISMISS_AFTER
  );
});

const recordSwipeHintUserSnap = (): void => {
  if (swipeHintSnapCount.value >= SWIPE_HINT_DISMISS_AFTER) return;

  swipeHintSnapCount.value += 1;
  sessionStorage.setItem(SWIPE_HINT_SESSION_KEY, String(swipeHintSnapCount.value));
};

const clearProgrammaticResultsScrollGuard = (): void => {
  if (resultsScrollGuardTimeout) {
    clearTimeout(resultsScrollGuardTimeout);
    resultsScrollGuardTimeout = null;
  }

  isProgrammaticResultsScroll.value = false;
  lastResultsScrollBehavior.value = null;
};

const markProgrammaticResultsScroll = (behavior: ResultsScrollBehavior): void => {
  clearProgrammaticResultsScrollGuard();

  isProgrammaticResultsScroll.value = true;
  lastResultsScrollBehavior.value = behavior;
  const timeoutMs = behavior === 'smooth' ? 420 : 100;
  resultsScrollGuardTimeout = setTimeout(() => {
    clearProgrammaticResultsScrollGuard();
  }, timeoutMs);
};

const getResultsContainer = (): HTMLElement | null => {
  return document.querySelector<HTMLElement>('#results');
};

/** Scroll snap does not always emit after programmatic `scrollTo`; mirror `handleSnappedSlide` side effects. */
const finalizeProgrammaticResultsScroll = async (card: ResultCard): Promise<void> => {
  await nextTick();
  const index = resultDeck.value.findIndex((c) => c.domId === card.domId);
  if (index < 0) {
    return;
  }

  activeSnapIndex.value = index;

  if (card.intent && card.kind !== 'prev_loader' && card.kind !== 'next_loader' && card.kind !== 'loading') {
    void syncActiveIntentFromLoadedResultDay(card.intent);
  }

  await syncLiveResultCardAfterSnap(index);
};

const scrollResultsToCard = async (
  card: ResultCard,
  options?: {
    // TODO: This isn't working anymore
    behavior?: ResultsScrollBehavior;
  },
): Promise<boolean> => {
  await nextTick();

  const container = getResultsContainer();
  const target = document.getElementById(card.domId);

  if (!container || !target) {
    return false;
  }

  const behavior = options?.behavior || 'instant';
  markProgrammaticResultsScroll(behavior);

  scrollTo(container, target, { behavior: 'instant' });

  await finalizeProgrammaticResultsScroll(card);

  return true;
};

const cloneFilteredResultList = (results: SparkFilteredResult[]): SparkFilteredResult[] => {
  return results.map((entry) => ({
    segment: entry.segment,
    proposal: entry.proposal,
  }));
};

const cloneSparkResultEntries = (
  entries: Array<{ segment: SegmentResult; proposal?: SegmentProposal }>,
): Array<{ segment: SegmentResult; proposal?: SegmentProposal }> => {
  return entries.map((entry) => ({
    segment: entry.segment,
    proposal: entry.proposal,
  }));
};

const cloneSparkIntentForSnapshot = (intent: SparkIntent): SparkIntent => {
  return {
    ...intent,
    timeSpan: { ...intent.timeSpan },
    locationIds: [...(intent.locationIds || [])],
    locations: intent.locations ? [...intent.locations] : intent.locations,
    availabilities: intent.availabilities ? [...intent.availabilities] : intent.availabilities,
    confirmations: intent.confirmations ? [...intent.confirmations] : intent.confirmations,
  };
};

const cloneSparkIntentResultsSnapshot = (snapshot: SparkIntentResultsSnapshot): SparkIntentResultsSnapshot => {
  return {
    ...snapshot,
    intent: cloneSparkIntentForSnapshot(snapshot.intent),
    intentResults: [...snapshot.intentResults],
    invitedProposals: [...snapshot.invitedProposals],
    sparkResults: cloneSparkResultEntries(snapshot.sparkResults),
    invitedChallenges: [...snapshot.invitedChallenges],
    invitedConfirmedGambits: [...snapshot.invitedConfirmedGambits],
    confirmedGambits: [...snapshot.confirmedGambits],
  };
};

const buildEmptyIntentResultsSnapshot = (intent: SparkIntent): SparkIntentResultsSnapshot => {
  return {
    intent: cloneSparkIntentForSnapshot(intent),
    intentResults: [],
    invitedProposals: [],
    invitedConfirmedGambits: [],
    confirmedGambits: [],
    invitedChallenges: [],
    sparkResults: [],
    fetchedAt: new Date().toISOString(),
  };
};

const buildActiveIntentResultsSnapshot = (intent: SparkIntent): SparkIntentResultsSnapshot => {
  return {
    intent: cloneSparkIntentForSnapshot(intent),
    intentResults: [...intentResults.value],
    invitedProposals: [...invitedProposals.value],
    invitedChallenges: [...invitedChallenges.value],
    invitedConfirmedGambits: [...invitedConfirmedGambits.value],
    confirmedGambits: [...confirmedGambits.value],
    sparkResults: cloneSparkResultEntries(sparkResults.value),
    fetchedAt: new Date().toISOString(),
  };
};

const resolveUnavailableReason = (intent: SparkIntent): UnavailableReason | null => {
  if (intent.isDisabled) return 'day';
  return null;
};

const collectUnansweredInvitedProposalIds = (proposals: SegmentProposal[]): Set<string> => {
  return new Set(
    proposals
      .filter((proposal) => hasUnansweredSparkInvitation(proposal, userDetails.value?.id))
      .map((proposal) => proposal.id),
  );
};

const buildLoadedResultDaySnapshot = (intent: SparkIntent): LoadedResultDay => {
  const reason = resolveUnavailableReason(intent);
  return {
    intent,
    isDisabled: !!intent.isDisabled,
    unavailableReason: reason,
    invitedChallenges: sortSparkGambitsByAttendees(invitedChallenges.value),
    invitedConfirmedGambits: sortSparkGambitsByAttendees(invitedConfirmedGambits.value),
    confirmedGambits: sortSparkGambitsByAttendees(confirmedGambits.value),
    filteredResults: cloneFilteredResultList(filteredResults.value),
    invitedProposalIds: collectUnansweredInvitedProposalIds(invitedProposals.value),
    sourceSnapshot: buildActiveIntentResultsSnapshot(intent),
  };
};

const buildLoadedResultDaySnapshotFromStoreSnapshot = (snapshot: SparkIntentResultsSnapshot): LoadedResultDay => {
  const canonicalIntent = cloneSparkIntentForSnapshot(snapshot.intent);
  const reason = resolveUnavailableReason(canonicalIntent);

  return {
    intent: canonicalIntent,
    isDisabled: !!canonicalIntent.isDisabled,
    unavailableReason: reason,
    invitedChallenges: sortSparkGambitsByAttendees(snapshot.invitedChallenges),
    invitedConfirmedGambits: sortSparkGambitsByAttendees(snapshot.invitedConfirmedGambits),
    confirmedGambits: sortSparkGambitsByAttendees(snapshot.confirmedGambits),
    filteredResults: filterVisibleSparkResults(snapshot.sparkResults, attendedGambits.value, snapshot.confirmedGambits),
    invitedProposalIds: collectUnansweredInvitedProposalIds(snapshot.invitedProposals),
    sourceSnapshot: cloneSparkIntentResultsSnapshot(snapshot),
  };
};

const buildLoadedResultDayShell = (intent: SparkIntent, reason?: UnavailableReason): LoadedResultDay => {
  const isDisabled = !!intent.isDisabled;
  return {
    intent,
    isDisabled,
    unavailableReason: reason ?? resolveUnavailableReason(intent),
    invitedChallenges: [],
    invitedConfirmedGambits: [],
    confirmedGambits: [],
    filteredResults: [],
    invitedProposalIds: new Set(),
    sourceSnapshot: isDisabled ? buildEmptyIntentResultsSnapshot(intent) : null,
  };
};

const upsertLoadedResultDaySorted = (nextDay: LoadedResultDay): void => {
  const existingIndex = loadedResultDays.value.findIndex((day) => day.intent.id === nextDay.intent.id);
  const nextDays = [...loadedResultDays.value];

  if (existingIndex >= 0) {
    nextDays.splice(existingIndex, 1, nextDay);
  } else {
    nextDays.push(nextDay);
  }

  nextDays.sort((left, right) => left.intent.date.localeCompare(right.intent.date));
  loadedResultDays.value = nextDays;
};

const replaceLoadedResultDaySnapshot = (snapshot: LoadedResultDay): void => {
  const existingIndex = loadedResultDays.value.findIndex((day) => day.intent.id === snapshot.intent.id);

  if (existingIndex < 0) {
    return;
  }

  upsertLoadedResultDaySorted(snapshot);
};

const setLoadedResultDaysToSingle = (snapshot: LoadedResultDay): void => {
  loadedResultDays.value = [snapshot];
  previousResultDayError.value = null;
};

const resetLoadedResultDaysToCurrent = (intent: SparkIntent): void => {
  setLoadedResultDaysToSingle(buildLoadedResultDaySnapshot(intent));
};

const appendLoadedResultDayFromCurrent = (intent: SparkIntent): void => {
  upsertLoadedResultDaySorted(buildLoadedResultDaySnapshot(intent));
  previousResultDayError.value = null;
};

const appendLoadedResultDayShell = (intent: SparkIntent): void => {
  upsertLoadedResultDaySorted(buildLoadedResultDayShell(intent));
};

const findLoadedResultDayByIntentId = (intentId: string): LoadedResultDay | undefined => {
  return loadedResultDays.value.find((day) => day.intent.id === intentId);
};

const resolveIntentById = (intentId: string): SparkIntent | null => {
  if (sparkIntent.value?.id === intentId) {
    return sparkIntent.value;
  }

  const intentFromIntents = intents.value.find((intent) => intent.id === intentId);

  if (intentFromIntents) {
    return intentFromIntents;
  }

  return findLoadedResultDayByIntentId(intentId)?.intent || null;
};

const syncActiveLoadedResultDay = (): void => {
  const activeIntentId = activeResultsIntentId.value;

  if (!activeIntentId) {
    return;
  }

  const activeIntent = resolveIntentById(activeIntentId);

  if (!activeIntent) {
    return;
  }

  replaceLoadedResultDaySnapshot(buildLoadedResultDaySnapshot(activeIntent));
};

const clearLoadedResultDays = (): void => {
  loadedResultDays.value = [];
  clearVotedCardHold();
  hasStaleTocResults.value = false;
  isLoadingNextResultDay.value = false;
  isLoadingPreviousResultDay.value = false;
  previousResultDayError.value = null;
  showNewResultsAbove.value = false;
  firstChangedCardToken.value = null;
  activeSnapIndex.value = 0;
  clearProgrammaticResultsScrollGuard();
  pendingFetchingResultsRoute.value = null;
  queuedFetchingResultsRoute.value = null;
  inFlightActiveResultsRefreshIntentIds.clear();
  teardownUnseenTocWatchers();
};

const bootstrapDiscoveryData = async (): Promise<void> => {
  if (discoveryBootstrapPromise) {
    await discoveryBootstrapPromise;
    return;
  }

  discoveryBootstrapPromise = (async () => {
    isDiscoveryBootstrapInFlight.value = true;

    if (!city.value?.id) {
      await cityStore.determineCity();
    }

    await sparkStore.ensureDailyAvailabilitiesExtended(city.value?.id);

    const start = new Date();
    const end = addDays(start, 7);

    await sparkStore.findMyGambits(start, end);
    await sparkStore.fetchIntents();
  })().finally(() => {
    isDiscoveryBootstrapInFlight.value = false;
    discoveryBootstrapPromise = null;
  });

  await discoveryBootstrapPromise;
};

const refreshMyGambits = () => sparkStore.findMyGambits(new Date(), addDays(new Date(), 7));

// Week preview

const resultDayTocDaysRef = ref<HTMLElement | null>(null);

const isWeekCardActive = computed(() => isWeekCard(activeResultCard.value));

const DECK_LOADER_KINDS = new Set(['loading', 'prev_loader', 'next_loader']);

/* The Week card is first, so it is the active card while the days are still
   loading; the chip only lights up once the player can actually be on it. */
const isWeekTocChipActive = computed(() => {
  if (!isWeekCardActive.value || sparkStep.value !== SparkStep.RESULTS) return false;
  if (isKeepAliveRestoring.value || isCitySwitchLoading.value) return false;

  return resultDeck.value.some((card) => !isWeekCard(card) && !DECK_LOADER_KINDS.has(card.kind));
});

/* Pink, the screen colour, while the week holds any row. Selecting it still
   wins and shows the white-on-grey active state. */
const hasWeekActivity = computed(() => (weekPreview.value?.items.length ?? 0) > 0);

const weekPreviewLocationIds = computed(() => {
  return Array.from(new Set(intents.value.flatMap((intent) => intent.locationIds || []))).sort();
});

const refreshWeekPreview = (): Promise<void> => {
  return weekPreviewStore.fetch(minVisibleResultsDateKey.value, { silent: true });
};

/* Proposals outside the player's availability have no card in the day deck, so
   those open the day itself instead of a "not found" card. */
const handleWeekPreviewPlay = () => {
  const today = resultDayTocEntries.value[0]?.date;
  if (today) {
    void handleResultDayTocClick(today);
  }
};

const handleWeekPreviewSelect = async (item: SparkWeekPreviewItem): Promise<void> => {
  const token = item.kind === 'gambit' ? getGambitCardToken(item.id) : getProposalCardToken(item.id);
  const loadedCard = resultDeck.value.find((card) => card.token === token && card.date === item.date);

  if (loadedCard) {
    activeSnapIndex.value = resultDeck.value.indexOf(loadedCard);
    await scrollResultsToCard(loadedCard, { behavior: 'instant' });
    setCanonicalResultURL(activeSnapIndex.value);
    return;
  }

  if (item.kind === 'proposal' && item.isOutsideAvailability && !item.myStatus) {
    await handleResultDayTocClick(item.date);
    return;
  }

  await router.push({
    name: RouterNameType.SPARKS,
    query: { date: item.date, view: token, from: 'card' },
  });
};

const scrollTocToActiveDay = (): void => {
  const container = resultDayTocDaysRef.value;
  const activeDay = container?.querySelector<HTMLElement>('.results-day-toc-item.is-active');

  if (!container || !activeDay || container.scrollWidth <= container.clientWidth) {
    return;
  }

  container.scrollTo({ left: activeDay.offsetLeft - container.offsetLeft, behavior: 'smooth' });
};

watch(
  () => (isKeepAliveActive.value && sparkIntent.value ? minVisibleResultsDateKey.value : null),
  (from) => {
    from && void weekPreviewStore.fetch(from);
  },
  { immediate: true },
);

watch(
  [weekPreviewLocationIds, isKeepAliveActive],
  ([locationIds, isActive]) => {
    isActive ? weekPreviewStore.subscribe(locationIds) : weekPreviewStore.unsubscribe();
  },
  { immediate: true },
);

watch(
  () => route.query.date,
  () => void nextTick(scrollTocToActiveDay),
);

const shouldShowCustomCard = (_intent?: SparkIntent): boolean => {
  return true;
};

const formatResultDayTocLabel = (date: Date): string => {
  return format(date, 'EEEEE').toUpperCase();
};

const tocItemOpacity = (dateKey: string): number => {
  return dateKey === dateParam.value ? 1 : 0.55;
};

// Lifecycle

onUnmounted(() => {
  clearProgrammaticResultsScrollGuard();

  sparkStep.value = SparkStep.WIZARD;
  currentScreen.value = SparkScreen.DAYS;
  currentProposalId.value = null;
  currentGambitId.value = null;
  isExactProposal.value = false;
  clearLoadedResultDays();
  isDiscoveryBootstrapInFlight.value = false;
  isRefreshResultsInFlight.value = false;
  proposalBootstrapInFlightKey.value = null;
  pendingTocTransientViewClearDate.value = null;
  discoveryBootstrapPromise = null;

  intentStore.clearIntentStore();
  proposalStore.clearProposalStore();
  sparkStore.clear();
  sparkStore.unsubscribeFromGambit?.();

  isInitializing.value = true;
});

// Route & Screen sync

const pendingTocTransientViewClearDate = ref<string | null>(null);

// Availabilities

const isAvailabilityModalOpen = ref(false);

async function refreshAvailabilityResults(savedDates?: string[]) {
  isApplyingAvailabilityResults = true;

  try {
    await sparkStore.fetchIntents({ refreshExisting: true });

    const routeDate = typeof route.query.date === 'string' ? route.query.date : null;
    const datesToRefresh = new Set<string>();

    if (savedDates?.length) {
      savedDates.forEach((date) => datesToRefresh.add(date));
    } else if (routeDate) {
      datesToRefresh.add(routeDate);
    }

    if (!savedDates?.length) {
      loadedResultDays.value.forEach((day) => datesToRefresh.add(day.intent.date));
    }

    const intentsToRefresh = [...datesToRefresh]
      .map(
        (date) =>
          pickPreferredIntentForDate(date) ||
          loadedResultDays.value.find((day) => day.intent.date === date)?.intent ||
          null,
      )
      .filter((intent): intent is SparkIntent => !!intent);

    await Promise.all(
      intentsToRefresh.map(async (intent) => {
        const latestIntent = pickPreferredIntentForDate(intent.date) || resolveIntentById(intent.id) || intent;
        const snapshot = await sparkStore.fetchIntentResultsSnapshot(latestIntent, {
          forceRefresh: true,
          silent: true,
        });
        const nextDay = buildLoadedResultDaySnapshotFromStoreSnapshot(snapshot);
        const isLoadedDay =
          !!findLoadedResultDayByIntentId(latestIntent.id) ||
          loadedResultDays.value.some((day) => day.intent.date === latestIntent.date);
        const isCurrentToc = routeDate === latestIntent.date;

        if (isLoadedDay || isCurrentToc) {
          upsertLoadedResultDaySorted(nextDay);
        }

        if (activeResultsIntentId.value === latestIntent.id || sparkIntent.value?.id === latestIntent.id) {
          sparkStore.applyActiveIntentResultsSnapshot(snapshot, latestIntent.id);
        }
      }),
    );

    hasStaleTocResults.value = false;
  } finally {
    isApplyingAvailabilityResults = false;
  }
}

async function handleConfirmAvailabilityModal(savedDates?: string[]) {
  await refreshAvailabilityResults(savedDates);
  await handleCloseAvailabilityModal();
}

async function handleCloseAvailabilityModal() {
  if (!isAvailabilityModalOpen.value) {
    return;
  }

  isAvailabilityModalOpen.value = false;
}

function handleAvailabilityClick() {
  if (isAvailabilityModalOpen.value) {
    return;
  }

  isAvailabilityModalOpen.value = true;
}

// Spark success

const markSuccessItemsAsRead = async (items: SuccessListDialogItem[]): Promise<void> => {
  if (!items.length) {
    return;
  }

  for (const item of items) {
    dismissedSuccessProposalIds.add(item.proposalId);
  }

  await Promise.all(
    items.map(async (item) => {
      const result = await sparkStore.markProposalSuccessAsRead(item.proposalId);

      if (!result.data) {
        logger.warn('Could not mark Spark success proposal as read.', {
          proposalId: item.proposalId,
          code: result.error?.code,
        });
      }
    }),
  );
};

const { open: openSparkSuccessListModal, close: closeSparkSuccessListModal } = useModal({
  component: SuccessListDialog,
  attrs: {
    title: localize('spark.success.title'),
    getItems: () => unreadSuccessItems.value,
    onSelect: async (item: SuccessListDialogItem) => {
      await markSuccessItemsAsRead([item]);

      showNewResultsAbove.value = false;
      firstChangedCardToken.value = null;
      suppressResultDaySync = false;

      const targetIntent =
        findEnabledIntentByDate(intents.value, item.date) ||
        findIntentByDate(intents.value, item.date) ||
        sparkIntent.value;

      if (targetIntent) {
        // Same-day confirm still needs a refresh so the proposal card becomes the gambit.
        if (sparkIntent.value?.id === targetIntent.id) {
          await sparkStore.findIntentAndGambitResults(targetIntent.id);
        }

        await handleResumeIntentGambit(targetIntent, item.gambitId);
      }

      unreadSuccessItems.value = [];
      await closeSparkSuccessListModal();
    },
  },
});

const { open: openGambitExistsModal, close: closeGambitExistsModal } = useModal({
  component: ConfirmDialog,
  attrs: {
    title: localize('spark.gambit_exists.title'),
    prompt: localize('spark.gambit_exists.prompt'),
    confirmLabel: localize('spark.gambit_exists.view'),
    cancelLabel: localize('app.verb.cancel'),
    onConfirm: async () => {
      const gambitId = sparkStore.gambitConflictId;
      sparkStore.gambitConflictId = null;
      await closeGambitExistsModal();
      if (gambitId) {
        router.push({ name: RouterNameType.GAMBIT, params: { id: gambitId }, query: { back: 'history' } });
      }
    },
    onCancel: async () => {
      sparkStore.gambitConflictId = null;
      await closeGambitExistsModal();
    },
  },
});

const flattenUnreadSuccessfulProposals = (): SuccessListDialogItem[] => {
  const uniqueByProposalId = new Set<string>();
  const flattened: SuccessListDialogItem[] = [];

  for (const intent of intentListItems.value) {
    const intentDate = intent.date;

    for (const unreadProposal of intent.unreadSuccessfulProposals || []) {
      if (
        !unreadProposal.proposalId ||
        !unreadProposal.gambitId ||
        uniqueByProposalId.has(unreadProposal.proposalId) ||
        dismissedSuccessProposalIds.has(unreadProposal.proposalId)
      ) {
        continue;
      }

      uniqueByProposalId.add(unreadProposal.proposalId);

      flattened.push({
        proposalId: unreadProposal.proposalId,
        gambitId: unreadProposal.gambitId,
        date: intentDate,
        timeSpanStart: unreadProposal.timeSpanStart,
        timeZone: intent.timeZone,
        locationName: unreadProposal.locationName,
      });
    }
  }

  return flattened;
};

const processUnreadSuccessfulProposals = async (): Promise<void> => {
  if (isUnreadSuccessProcessing.value || isJoiningProposal.value) {
    return;
  }

  const unreadItems = flattenUnreadSuccessfulProposals();

  if (!unreadItems.length) {
    return;
  }

  isUnreadSuccessProcessing.value = true;

  try {
    unreadSuccessItems.value = unreadItems;
    await openSparkSuccessListModal();
  } finally {
    isUnreadSuccessProcessing.value = false;
  }
};

watch(
  () =>
    intentListItems.value
      .map(
        (intent) =>
          `${intent.id}:${(intent.unreadSuccessfulProposals || [])
            .map((proposal) => `${proposal.proposalId}:${proposal.gambitId}`)
            .join(',')}`,
      )
      .join('|'),
  () => {
    if (!isKeepAliveActive.value) return;
    void processUnreadSuccessfulProposals();
  },
  { immediate: true },
);

watch(isJoiningProposal, (joiningProposal) => {
  if (!isKeepAliveActive.value) return;
  if (!joiningProposal) {
    void processUnreadSuccessfulProposals();
  }
});

// Keep synchronization tied to active payload ownership, not `sparkIntent`: metadata intent can switch
// before results payload ownership is rehydrated from route context during cross-day scroll.
watch(
  [
    activeResultsIntentId,
    filteredResults,
    () => gambitResults.value,
    () => confirmedGambits.value,
    () => invitedConfirmedGambits.value,
    () => invitedChallenges.value,
  ],
  () => {
    if (suppressResultDaySync || loadedResultDays.value.length) {
      return;
    }

    syncActiveLoadedResultDay();
  },
);

watch(sparkProposal, (updated) => {
  if (!isKeepAliveActive.value || !updated) return;
  sparkStore.replaceProposalInResults(updated);
  patchLoadedResultDayProposal(updated);
});

const resolveLiveViewingIntent = (): SparkIntent | null => {
  const activeCard = activeResultCard.value;

  if (
    !activeCard ||
    isWeekCard(activeCard) ||
    activeCard.kind === 'prev_loader' ||
    activeCard.kind === 'next_loader' ||
    isFetchingResultsRouteInFlight.value
  ) {
    return null;
  }

  const viewingDate = activeCard.date || sparkIntent.value?.date;

  if (!viewingDate) {
    return null;
  }

  if (sparkIntent.value?.date === viewingDate) {
    return sparkIntent.value;
  }

  return pickPreferredIntentForDate(viewingDate);
};

/* Loader cards and the Spark section carry no date of their own, but the deck
   still owns a day underneath them. Keep watching that day: a proposal somebody
   else just created is only ever noticed by the poll, so standing down here is
   what made new cards feel dead until you left the screen and came back. */
const resolveUnseenCheckIntent = (): SparkIntent | null => {
  const viewingIntent = resolveLiveViewingIntent();

  if (viewingIntent) {
    return viewingIntent;
  }

  const ownedIntentId = activeResultsIntentId.value;

  if (!ownedIntentId || isFetchingResultsRouteInFlight.value) {
    return null;
  }

  return resolveIntentById(ownedIntentId) || null;
};

watch(
  () => {
    const viewingIntent = resolveLiveViewingIntent();
    return viewingIntent ? `${viewingIntent.id}:${viewingIntent.updatedAt}` : '';
  },
  () => {
    if (!isKeepAliveActive.value || sparkStep.value !== SparkStep.RESULTS) {
      return;
    }

    const viewingIntent = resolveLiveViewingIntent();

    if (!viewingIntent) {
      return;
    }

    const loadedSnapshot = findLoadedResultDayByIntentId(viewingIntent.id)?.sourceSnapshot || null;
    maybeRefreshActiveResultsContextForIntent(viewingIntent, loadedSnapshot);
  },
);

watch(
  () => {
    const viewingIntent = resolveUnseenCheckIntent();

    if (!viewingIntent || sparkStep.value !== SparkStep.RESULTS) {
      return '';
    }

    const loadedDay = findLoadedResultDayByIntentId(viewingIntent.id);
    const proposalIds = (loadedDay?.filteredResults || [])
      .map((entry) => entry.proposal?.id)
      .filter((proposalId): proposalId is string => !!proposalId)
      .sort()
      .join('|');
    const gambitIds = collectLoadedDayGambitIds(loadedDay).sort().join('|');

    return `${viewingIntent.id}:${proposalIds}:${gambitIds}`;
  },
  (listenerKey) => {
    if (!listenerKey) {
      teardownDayProposalListeners();
      teardownDayGambitListeners();
      stopUnseenTocPoll();
      return;
    }

    syncDayProposalListeners();
    syncDayGambitListeners();
    startUnseenTocPoll();
  },
);

watch(refreshResultsSignal, () => {
  if (!isKeepAliveActive.value) return;
  handleRefreshResults();
});

watch(
  () => sparkStore.droppedGambitId,
  (gambitId) => {
    if (!gambitId) {
      return;
    }

    removeGambitFromLoadedDays(gambitId);
  },
);

watch(
  () => sparkStore.patchedGambit,
  (patched) => {
    if (!patched) {
      return;
    }

    loadedResultDays.value = loadedResultDays.value.map((day) => patchGambitInDay(day, patched.id, patched.patch));
  },
);

const refreshLoadedDayForGambit = (gambitId: string) => {
  sparkStore.clearGambitDeckRefresh();

  const day = findLoadedDayForGambit(gambitId) || findLoadedResultDayByIntentId(activeResultsIntentId.value || '');
  if (day) {
    void refreshLoadedDayGambitCards(day.intent);
  }
};

watch(
  () => sparkStore.gambitDeckRefreshId,
  (gambitId) => {
    if (!gambitId || !isKeepAliveActive.value) {
      return;
    }

    refreshLoadedDayForGambit(gambitId);
  },
);

watch(
  () => sparkStore.droppedProposalId,
  (proposalId) => {
    if (!proposalId) {
      return;
    }

    removeProposalFromLoadedDays(proposalId);
  },
);

watch(
  () => sparkStore.mergedProposalGambitId,
  (gambitId) => {
    if (!gambitId || !sparkStore.droppedProposalId) {
      return;
    }

    handleMergedProposal(sparkStore.droppedProposalId, gambitId);
  },
);

watch(gotoNewProposalSignal, () => {
  if (!isKeepAliveActive.value) return;
  isKeepAliveRestoring.value = false;
  handleCreateProposalRoute();
});

watch(openAvailabilitiesSignal, () => {
  if (!isKeepAliveActive.value) return;
  handleAvailabilityClick();
});

const handleCreateProposalRoute = async () => {
  router.push({
    ...route,
    query: {
      ...route.query,
      date: chosenDate.value || sparkIntent.value?.date || route.query.date,
      view: 'new',
    },
  });
};

// Navigation actions

const activateIntent = async (intent: SparkIntent) => {
  if (sparkIntent.value?.id === intent.id) {
    return;
  }

  intentStore.replaceIntent(intent);

  const { timeZone, timeSpan } = intent;

  startHour.value = Number.parseInt(formatInTimeZone(timeSpan.start, timeZone, 'H'), 10);
  endHour.value = Number.parseInt(formatInTimeZone(timeSpan.end, timeZone, 'H'), 10);

  // TODO: Require reloading sparkable locations onMounted instead

  await sparkStore.findIntentAndGambitResults(intent.id);

  timeSpanKey.value = intent.id;
};

const prepareResultsDeckBeforeWizardExit = async (
  intent: SparkIntent,
  preferredCardToken: string | null | undefined,
): Promise<void> => {
  const normalizedPreferredToken = normalizeRouteViewToken(preferredCardToken || undefined);
  let targetIndex = normalizedPreferredToken ? findDeckIndexForRouteCard(intent.date, normalizedPreferredToken) : -1;

  if (targetIndex < 0) {
    targetIndex = findFallbackDeckIndex(intent.date);
  }

  if (targetIndex < 0) {
    targetIndex = 0;
  }

  const targetCard = resultDeck.value[targetIndex] || null;
  activeSnapIndex.value = targetIndex;

  if (targetCard) {
    await scrollResultsToCard(targetCard, {
      behavior: 'instant',
    });
  }
};

const isCameraCardViewToken = (view?: string): boolean => {
  if (!view) {
    return false;
  }

  return view.startsWith('proposal:') || view.startsWith('gambit:') || view.startsWith('match:');
};

const isDirectCardViewToken = (view?: string): boolean => {
  return !!view && (view.startsWith('proposal:') || view.startsWith('gambit:'));
};

const isExplicitSparksCardOpen = (query: { from?: unknown; action?: unknown } = route.query): boolean => {
  return query.from === 'card' || query.action === 'results';
};

const stripExplicitCardOpenFromRoute = async () => {
  if (!isExplicitSparksCardOpen()) {
    return;
  }

  const { from: _from, action: _action, ...query } = route.query;
  await router.replace({
    name: RouterNameType.SPARKS,
    query,
  });
};

const findLiveDeckCardForToken = (
  date: string | undefined,
  token: string,
): { index: number; card: ResultCard } | null => {
  const index = findDeckIndexForRouteCard(date, token);
  if (index < 0) {
    return null;
  }

  const card = resultDeck.value[index];
  if (!card || isSkeletonResultCard(card)) {
    return null;
  }

  return { index, card };
};

const isAlreadyShowingCard = (date: string | undefined, token: string): boolean => {
  if (sparkStep.value !== SparkStep.RESULTS) {
    return false;
  }

  const found = findLiveDeckCardForToken(date, token);
  if (!found) {
    return false;
  }

  const activeCard = resultDeck.value[activeSnapIndex.value];
  return found.index === activeSnapIndex.value && activeCard?.token === token && (!date || activeCard.date === date);
};

const seekExistingCardInDeck = async (date: string | undefined, token: string): Promise<boolean> => {
  if (sparkStep.value !== SparkStep.RESULTS) {
    return false;
  }

  const found = findLiveDeckCardForToken(date, token);
  if (!found) {
    return false;
  }

  sparkStep.value = SparkStep.RESULTS;
  currentScreen.value = SparkScreen.RESULTS;

  if (found.index !== activeSnapIndex.value) {
    activeSnapIndex.value = found.index;
    await scrollResultsToCard(found.card, { behavior: 'instant' });
  }

  await stripExplicitCardOpenFromRoute();
  return true;
};

const isBrowserReload = (): boolean => {
  if (typeof performance === 'undefined') {
    return false;
  }

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return navigation?.type === 'reload';
};

let hasConsumedReloadCameraView = false;

const shouldIgnoreLeftoverCameraView = (view?: string): boolean => {
  if (!isCameraCardViewToken(view) || hasConsumedReloadCameraView) {
    return false;
  }

  if (!isBrowserReload()) {
    return false;
  }

  hasConsumedReloadCameraView = true;
  return true;
};

const handleResumeIntentDay = async (intent: SparkIntent, cardToken?: string) => {
  sparkStore.clearPriorityProposalId();
  clearMissingProposalState();

  await activateIntent(intent);

  if (hasLeftSparks()) return;

  resetLoadedResultDaysToCurrent(intent);

  const resolvedCardToken = normalizeRouteViewToken(cardToken) || resolveFallbackCardTokenForDate(intent.date);
  await prepareResultsDeckBeforeWizardExit(intent, resolvedCardToken);

  if (hasLeftSparks()) return;

  if (!cardToken) {
    await memoizeResultsQuery(intent.date, findPrioritizedResultCardForDate(intent.date)?.token);
  }

  sparkStep.value = SparkStep.RESULTS;
  currentScreen.value = SparkScreen.RESULTS;
};

const handleResumeIntentProposal = async (date: string, id: string) => {
  const bootstrapKey = buildProposalBootstrapKey(date, id);

  if (proposalBootstrapInFlightKey.value === bootstrapKey) {
    return;
  }

  const readLinkedProposalState = () => {
    const proposalOrder = sparkResults.value
      .map((entry) => entry.proposal?.id)
      .filter((proposalId): proposalId is string => Boolean(proposalId));
    const resultIndex = proposalOrder.indexOf(id);

    return {
      resultIndex,
      proposalOrder,
    };
  };

  proposalBootstrapInFlightKey.value = bootstrapKey;
  clearMissingProposalState();

  try {
    await showWizardLoader();

    if (hasLeftSparks()) return;

    let intent = findIntentByDate(intents.value, date);
    if (!intent) {
      intent = await sparkStore.resolveIntentByDate(date);
    }

    if (hasLeftSparks()) return;

    if (!intent) {
      await proposalStore.fetchProposal(id);

      if (hasLeftSparks()) return;

      if (proposalStore.mergedIntoGambitId) {
        sparkStore.clearPriorityProposalId();
        router.push({
          name: RouterNameType.GAMBIT,
          params: { id: proposalStore.mergedIntoGambitId },
          query: { back: 'history' },
        });
        return;
      }

      if (isRefreshResultsInFlight.value || isFetchingResultsRouteInFlight.value) {
        sparkStore.clearPriorityProposalId();
        await redirectToCreatedIntentResults(clampResultsDateToVisibleWindow(date));
        return;
      }

      // Proposal is expired and no intent exists for its date — show "no longer available"
      // on the nearest visible intent.
      const fallbackDate = clampResultsDateToVisibleWindow(date);
      const fallbackIntent = findEnabledIntentByDate(intents.value, fallbackDate);

      if (!fallbackIntent) {
        sparkStore.clearPriorityProposalId();
        await redirectToCreatedIntentResults(fallbackDate);
        return;
      }

      await activateIntent(fallbackIntent);
      if (hasLeftSparks()) return;
      resetLoadedResultDaysToCurrent(fallbackIntent);

      upsertMissingProposalState({
        proposalId: id,
        intentId: fallbackIntent.id,
        date: fallbackIntent.date,
        phase: 'missing',
      });

      const targetCardToken = getProposalCardToken(id);
      await prepareResultsDeckBeforeWizardExit(fallbackIntent, targetCardToken);
      sparkStep.value = SparkStep.RESULTS;
      currentScreen.value = SparkScreen.RESULTS;
      return;
    }

    await Promise.all([activateIntent(intent), proposalStore.fetchProposal(id)]);

    if (hasLeftSparks()) return;

    if (proposalStore.mergedIntoGambitId) {
      const gambitId = proposalStore.mergedIntoGambitId;
      sparkStore.clearPriorityProposalId();
      clearMissingProposalState();
      return handleResumeIntentGambit(intent, gambitId);
    }

    resetLoadedResultDaysToCurrent(intent);

    let { resultIndex, proposalOrder } = readLinkedProposalState();

    if (resultIndex < 0) {
      try {
        await Promise.all([sparkStore.findIntentAndGambitResults(intent.id), proposalStore.fetchProposal(id)]);
        resetLoadedResultDaysToCurrent(intent);
      } catch (error) {}

      if (hasLeftSparks()) return;

      if (proposalStore.mergedIntoGambitId) {
        const gambitId = proposalStore.mergedIntoGambitId;
        sparkStore.clearPriorityProposalId();
        clearMissingProposalState();
        return handleResumeIntentGambit(intent, gambitId);
      }

      const nextState = readLinkedProposalState();
      resultIndex = nextState.resultIndex;
      proposalOrder = nextState.proposalOrder;
    }

    const isMissing = resultIndex < 0;
    const shouldKeepMissingCard = isMissing && !isRefreshResultsInFlight.value && !isFetchingResultsRouteInFlight.value;

    if (isMissing && !shouldKeepMissingCard) {
      sparkStore.clearPriorityProposalId();
      clearMissingProposalState();
      return handleResumeIntentDay(intent);
    }

    if (isMissing) {
      sparkStore.setPriorityProposalId(id, intent.id);
      resetLoadedResultDaysToCurrent(intent);
      upsertMissingProposalState({
        proposalId: id,
        intentId: intent.id,
        date: intent.date,
        phase: 'missing',
      });
    } else {
      sparkStore.clearPriorityProposalId();
      clearMissingProposalState();
    }

    if (hasLeftSparks()) return;

    const currentRouteViewToken = normalizeRouteViewToken(
      typeof route.query.view === 'string' ? route.query.view : undefined,
    );
    const isStillTargetProposalRoute = route.query.date === date && currentRouteViewToken === getProposalCardToken(id);
    const isRecoverableEndRoute = resultIndex >= 0 && route.query.date === date && currentRouteViewToken === 'end';

    if (!isStillTargetProposalRoute && isRecoverableEndRoute) {
    }

    if (!isMissing && !isStillTargetProposalRoute && !isRecoverableEndRoute) {
      return;
    }

    const targetCardToken = getProposalCardToken(id);

    if (isMissing && !isStillTargetProposalRoute) {
      await router.replace({
        name: RouterNameType.SPARKS,
        query: buildResultsQuery(intent.date, { proposalId: id }),
      });
    }

    await prepareResultsDeckBeforeWizardExit(intent, targetCardToken);

    sparkStep.value = SparkStep.RESULTS;
    currentScreen.value = SparkScreen.RESULTS;
  } finally {
    if (proposalBootstrapInFlightKey.value === bootstrapKey) {
      proposalBootstrapInFlightKey.value = null;
    }

    isScrollLocked.value = false;
    await stripExplicitCardOpenFromRoute();
  }
};

const handleResumeIntentGambit = async (intent: SparkIntent, id: string) => {
  sparkStore.clearPriorityProposalId();
  clearMissingProposalState();
  await activateIntent(intent);
  if (hasLeftSparks()) return;
  resetLoadedResultDaysToCurrent(intent);

  const gambitToken = getGambitCardToken(id);
  const gambitCardIndex = resultDeck.value.findIndex((card) => card.token === gambitToken);

  if (gambitCardIndex < 0) {
    const sparksQuery = buildResultsQuery(intent.date, { gambitId: id });
    lastSparksQuery = { ...sparksQuery } as Record<string, string>;
    await router.replace({
      name: RouterNameType.SPARKS,
      query: sparksQuery,
    });
    if (hasLeftSparks()) return;
    await router.push({ name: RouterNameType.GAMBIT, params: { id }, query: { back: 'history' } });
    return;
  }

  await prepareResultsDeckBeforeWizardExit(intent, gambitToken);
  if (hasLeftSparks()) return;
  sparkStep.value = SparkStep.RESULTS;
  currentScreen.value = SparkScreen.RESULTS;
  await stripExplicitCardOpenFromRoute();
};

const handleResumeIntentExact = async (intent: SparkIntent) => {
  sparkStore.clearPriorityProposalId();
  clearMissingProposalState();
  await activateIntent(intent);
  if (hasLeftSparks()) return;
  resetLoadedResultDaysToCurrent(intent);
  const exactCardToken = shouldShowCustomCard(intent) ? 'new' : resolveFallbackCardTokenForDate(intent.date);
  await prepareResultsDeckBeforeWizardExit(intent, exactCardToken);
  if (hasLeftSparks()) return;
  sparkStep.value = SparkStep.RESULTS;
  currentScreen.value = SparkScreen.RESULTS;

  if (!shouldShowCustomCard(intent)) {
    isExactProposal.value = false;
    currentProposalId.value = null;
    currentGambitId.value = null;
    return;
  }
};

const resolveRefreshTargetIntent = (anchorDate: string | null): SparkIntent | null => {
  const sameDateIntent = anchorDate ? pickPreferredIntentForDate(anchorDate) : null;

  if (sameDateIntent) {
    return sameDateIntent;
  }

  return earliestEnabledIntent.value;
};

const resolveCurrentTocDate = (): string | null => {
  const activeCard = activeResultCard.value;

  if (isWeekCard(activeCard)) {
    return WEEK_TOC_DATE;
  }

  if (activeCard?.date && isValidDateString(activeCard.date)) {
    return activeCard.date;
  }

  const routeDate = typeof route.query.date === 'string' ? route.query.date : null;

  if (routeDate && isValidDateString(routeDate)) {
    return routeDate;
  }

  if (sparkIntent.value?.date && isValidDateString(sparkIntent.value.date)) {
    return sparkIntent.value.date;
  }

  return defaultResultsDateKey.value;
};

const findFirstCardForTocDate = (tocDate: string): ResultCard | null => {
  if (tocDate === WEEK_TOC_DATE) {
    return resultDeck.value.find(isWeekCard) || null;
  }

  return findPrioritizedResultCardForDate(tocDate);
};

const handleRefreshResults = async () => {
  if (isRefreshResultsInFlight.value) {
    return;
  }

  isRefreshResultsInFlight.value = true;
  isScrollFrozen.value = true;
  clearVotedCardHold();
  sparkStore.clearPriorityProposalId();
  clearMissingProposalState();
  hasStaleTocResults.value = false;
  const tocDate = resolveCurrentTocDate();

  showNewResultsAbove.value = false;
  firstChangedCardToken.value = null;

  try {
    await Promise.all([sparkStore.fetchIntents({ refreshExisting: true }), refreshMyGambits()]);

    if (tocDate === WEEK_TOC_DATE) {
      await refreshWeekPreview();
      const weekCard = findFirstCardForTocDate(WEEK_TOC_DATE);
      const scrollTargetIndex = weekCard ? resultDeck.value.indexOf(weekCard) : 0;
      activeSnapIndex.value = Math.max(scrollTargetIndex, 0);
      const scrollTargetCard = resultDeck.value[activeSnapIndex.value];

      if (scrollTargetCard) {
        await scrollResultsToCard(scrollTargetCard, { behavior: 'instant' });
      }

      await nextTick();
      isScrollFrozen.value = false;
      sparkStep.value = SparkStep.RESULTS;
      currentScreen.value = SparkScreen.RESULTS;
      return;
    }

    const targetIntent = resolveRefreshTargetIntent(tocDate);

    if (!targetIntent) {
      await redirectToFallbackResults(tocDate || defaultResultsDateKey.value);
      return;
    }

    await activateIntent(targetIntent);
    await sparkStore.findIntentAndGambitResults(targetIntent.id);

    resetLoadedResultDaysToCurrent(targetIntent);
    await nextTick();

    const tocTopCard = findFirstCardForTocDate(targetIntent.date);
    const scrollTargetIndex = tocTopCard ? resultDeck.value.indexOf(tocTopCard) : 0;
    activeSnapIndex.value = Math.max(scrollTargetIndex, 0);

    const scrollTargetCard = resultDeck.value[activeSnapIndex.value];

    if (hasLeftSparks()) return;

    await router.push({
      name: RouterNameType.SPARKS,
      query: buildResultsQuery(targetIntent.date, { viewToken: scrollTargetCard?.token }),
    });

    if (scrollTargetCard) {
      await scrollResultsToCard(scrollTargetCard, { behavior: 'instant' });
    }

    await nextTick();
    isScrollFrozen.value = false;

    sparkStep.value = SparkStep.RESULTS;
    currentScreen.value = SparkScreen.RESULTS;
  } catch (error) {
    logger.warn('Failed to refresh Spark intents/results', {
      tocDate,
      error,
    });

    if (sparkIntent.value) {
      sparkStep.value = SparkStep.RESULTS;
      currentScreen.value = SparkScreen.RESULTS;
    }
  } finally {
    isScrollLocked.value = false;
    isRefreshResultsInFlight.value = false;
    isScrollFrozen.value = false;
  }
};

const handleScrollToNewResults = async () => {
  const token = firstChangedCardToken.value;
  const targetCard = token
    ? resultDeck.value.find((card) => card.token === token)
    : resultDeck.value.find(
        (card) =>
          !isWeekCard(card) && card.kind !== 'prev_loader' && card.kind !== 'next_loader' && card.kind !== 'loading',
      );
  if (!targetCard) return;

  const targetIndex = resultDeck.value.indexOf(targetCard);
  if (targetIndex < 0) return;

  showNewResultsAbove.value = false;
  firstChangedCardToken.value = null;

  const container = getResultsContainer();
  if (!container) return;

  const slideHeight = container.clientHeight;
  markProgrammaticResultsScroll('instant');
  container.scrollTo({ top: targetIndex * slideHeight, behavior: 'instant' });
  await finalizeProgrammaticResultsScroll(targetCard);
};

const syncIntentMetadataForResultIntent = async (intent: SparkIntent): Promise<void> => {
  if (sparkIntent.value?.id !== intent.id) {
    intentStore.replaceIntent(intent);
  }

  const { timeZone, timeSpan } = intent;
  startHour.value = Number.parseInt(formatInTimeZone(timeSpan.start, timeZone, 'H'), 10);
  endHour.value = Number.parseInt(formatInTimeZone(timeSpan.end, timeZone, 'H'), 10);
  timeSpanKey.value = intent.id;

  // TODO: Require reloading sparkable locations onMounted instead
};

const syncActiveIntentFromLoadedResultDay = (intent: SparkIntent): void => {
  void syncIntentMetadataForResultIntent(intent);
};

const resolveImmediateSnapshotForIntent = async (intent: SparkIntent): Promise<SparkIntentResultsSnapshot> => {
  const loadedSnapshot = findLoadedResultDayByIntentId(intent.id)?.sourceSnapshot;

  if (loadedSnapshot) {
    return cloneSparkIntentResultsSnapshot(loadedSnapshot);
  }

  return sparkStore.fetchIntentResultsSnapshot(intent, { silent: true });
};

const maybeRefreshActiveResultsContextForIntent = (
  intent: SparkIntent,
  immediateSnapshot: SparkIntentResultsSnapshot | null,
): void => {
  const latestIntentForRefresh = resolveIntentById(intent.id) || intent;
  const liveUpdatedAt =
    sparkIntent.value?.id === latestIntentForRefresh.id
      ? sparkIntent.value.updatedAt
      : latestIntentForRefresh.updatedAt;

  const shouldRefreshSnapshot = !immediateSnapshot || immediateSnapshot.intent.updatedAt !== liveUpdatedAt;

  if (!shouldRefreshSnapshot || inFlightActiveResultsRefreshIntentIds.has(latestIntentForRefresh.id)) {
    return;
  }

  inFlightActiveResultsRefreshIntentIds.add(latestIntentForRefresh.id);

  void (async () => {
    try {
      const refreshedSnapshot = await sparkStore.fetchIntentResultsSnapshot(latestIntentForRefresh, {
        forceRefresh: true,
        silent: true,
      });
      markStaleIfUnseenDayChanges(latestIntentForRefresh, refreshedSnapshot);
    } catch (error) {
      logger.warn('Failed to refresh route-driven results snapshot', {
        intentId: latestIntentForRefresh.id,
        intentDate: latestIntentForRefresh.date,
        error,
      });
    } finally {
      inFlightActiveResultsRefreshIntentIds.delete(latestIntentForRefresh.id);
    }
  })();
};

const markStaleIfUnseenDayChanges = (intent: SparkIntent, refreshedSnapshot: SparkIntentResultsSnapshot): void => {
  /* The snapshot the deck was built from *is* the seen state, so entering a day
     leaves nothing unseen and there is no separate baseline to keep in sync. */
  if (isApplyingAvailabilityResults) {
    return;
  }

  const appliedSnapshot = findLoadedResultDayByIntentId(intent.id)?.sourceSnapshot;

  if (!appliedSnapshot) {
    return;
  }

  const excludeProposalIds = new Set(
    [heldVoteProposalId.value, activeResultCard.value?.result?.proposal?.id].filter(
      (proposalId): proposalId is string => !!proposalId,
    ),
  );
  const excludeGambitId = activeResultCard.value?.gambit?.id || null;

  const appliedFingerprint = getSnapshotOccupancyFingerprint(appliedSnapshot, excludeProposalIds, excludeGambitId);
  const nextFingerprint = getSnapshotOccupancyFingerprint(refreshedSnapshot, excludeProposalIds, excludeGambitId);

  /* A self action patches the deck optimistically, so a fetch that crosses it can
     briefly disagree. Those windows are skipped rather than coloured. */
  if (appliedFingerprint !== nextFingerprint && !ignoreUnseenTocColor && !isJoiningProposal.value) {
    hasStaleTocResults.value = true;
  }

  ignoreUnseenTocColor = false;
};

const evaluateUnseenTocChanges = (intent?: SparkIntent | null): void => {
  const viewingIntent = intent || resolveUnseenCheckIntent();

  if (
    !viewingIntent ||
    hasStaleTocResults.value ||
    isJoiningProposal.value ||
    !isKeepAliveActive.value ||
    /* A dead connection has no news to report, and the fetch would only throw. */
    navigator.onLine === false ||
    sparkStep.value !== SparkStep.RESULTS ||
    inFlightActiveResultsRefreshIntentIds.has(viewingIntent.id)
  ) {
    return;
  }

  inFlightActiveResultsRefreshIntentIds.add(viewingIntent.id);

  void (async () => {
    try {
      const refreshedSnapshot = await sparkStore.fetchIntentResultsSnapshot(viewingIntent, {
        forceRefresh: true,
        silent: true,
      });
      markStaleIfUnseenDayChanges(viewingIntent, refreshedSnapshot);
    } catch (error) {
      logger.warn('Failed to evaluate unseen Spark TOC changes', {
        intentId: viewingIntent.id,
        intentDate: viewingIntent.date,
        error,
      });
    } finally {
      inFlightActiveResultsRefreshIntentIds.delete(viewingIntent.id);
    }
  })();
};

const syncDayProposalListeners = (): void => {
  const viewingIntent = resolveUnseenCheckIntent();
  const loadedDay = viewingIntent ? findLoadedResultDayByIntentId(viewingIntent.id) : null;
  const nextProposalIds = new Set(
    (loadedDay?.filteredResults || [])
      .map((entry) => entry.proposal?.id)
      .filter((proposalId): proposalId is string => !!proposalId),
  );

  for (const [proposalId, unsubscribe] of dayProposalUnsubscribes) {
    if (!nextProposalIds.has(proposalId)) {
      unsubscribe();
      dayProposalUnsubscribes.delete(proposalId);
    }
  }

  nextProposalIds.forEach((proposalId) => {
    if (dayProposalUnsubscribes.has(proposalId)) {
      return;
    }

    let isBootstrapSnapshot = true;
    const unsubscribe = onSnapshot(doc(firestore, 'proposals', proposalId), (snapshot) => {
      const data = snapshot.exists() ? (snapshot.data() as { deleted?: boolean; mergedIntoGambitId?: string }) : null;
      const mergedIntoGambitId = data?.mergedIntoGambitId;
      const isMerged = !!data?.deleted && !!mergedIntoGambitId;

      if (isBootstrapSnapshot) {
        isBootstrapSnapshot = false;
        if (isMerged && mergedIntoGambitId) {
          sparkStore.mergeProposalIntoGambit(proposalId, mergedIntoGambitId);
        }
        return;
      }

      if (isMerged && mergedIntoGambitId) {
        sparkStore.mergeProposalIntoGambit(proposalId, mergedIntoGambitId);
        return;
      }

      /* Reconnecting replays every watched document from cache at once. Those are
         not news, and treating them as news is what lit the CTA on the way back
         from a dead connection. */
      if (snapshot.metadata.fromCache) {
        return;
      }

      const viewedProposalId = activeResultCard.value?.result?.proposal?.id;
      if (
        proposalId === viewedProposalId ||
        proposalId === heldVoteProposalId.value ||
        isJoiningProposal.value ||
        ignoreUnseenTocColor
      ) {
        return;
      }

      evaluateUnseenTocChanges();
    });

    dayProposalUnsubscribes.set(proposalId, unsubscribe);
  });
};

const syncDayGambitListeners = (): void => {
  const viewingIntent = resolveUnseenCheckIntent();
  const loadedDay = viewingIntent ? findLoadedResultDayByIntentId(viewingIntent.id) : null;
  const nextGambitIds = new Set(collectLoadedDayGambitIds(loadedDay));

  for (const [gambitId, unsubscribe] of dayGambitUnsubscribes) {
    if (!nextGambitIds.has(gambitId)) {
      unsubscribe();
      dayGambitUnsubscribes.delete(gambitId);
    }
  }

  nextGambitIds.forEach((gambitId) => {
    if (dayGambitUnsubscribes.has(gambitId)) {
      return;
    }

    let isBootstrapSnapshot = true;
    let didExist = false;
    const unsubscribe = onSnapshot(doc(firestore, 'gambits', gambitId), (snapshot) => {
      if (isBootstrapSnapshot) {
        isBootstrapSnapshot = false;
        didExist = snapshot.exists();
        return;
      }

      if (snapshot.metadata.fromCache) {
        return;
      }

      if (!snapshot.exists()) {
        if (didExist) {
          removeGambitFromLoadedDays(gambitId);
          sparkStore.dropGambitFromResults(gambitId);
        }
        return;
      }

      const data = snapshot.data() as { deleted?: boolean; mergedIntoGambitId?: string };
      if (data.deleted && data.mergedIntoGambitId) {
        removeGambitFromLoadedDays(gambitId);
        sparkStore.dropGambitFromResults(gambitId);

        if (isViewingGambit(gambitId)) {
          const day =
            findLoadedDayForGambit(data.mergedIntoGambitId) ||
            findLoadedResultDayByIntentId(activeResultsIntentId.value || '') ||
            loadedResultDays.value[0];
          if (day) {
            void handleResumeIntentGambit(day.intent, data.mergedIntoGambitId);
          } else {
            void router.push({
              name: RouterNameType.GAMBIT,
              params: { id: data.mergedIntoGambitId },
              query: { back: 'history' },
            });
          }
        } else {
          const day =
            findLoadedDayForGambit(data.mergedIntoGambitId) ||
            findLoadedResultDayByIntentId(activeResultsIntentId.value || '');
          if (day) {
            void refreshLoadedDayGambitCards(day.intent);
          }
        }
        return;
      }

      didExist = true;
      const day = findLoadedDayForGambit(gambitId);
      if (!day) {
        return;
      }

      void refreshLoadedDayGambitCards(day.intent);
    });

    dayGambitUnsubscribes.set(gambitId, unsubscribe);
  });
};

const startUnseenTocPoll = (): void => {
  if (unseenTocPollTimer) {
    return;
  }

  unseenTocPollTimer = setInterval(() => {
    if (!isKeepAliveActive.value || sparkStep.value !== SparkStep.RESULTS) {
      return;
    }

    /* The week card has no day snapshot to diff; the preview's fingerprint is the
       cheap check, and the store only swaps data when it changes. */
    if (isWeekCardActive.value) {
      navigator.onLine !== false && void refreshWeekPreview();
      return;
    }

    if (hasStaleTocResults.value) {
      return;
    }

    evaluateUnseenTocChanges();
  }, UNSEEN_TOC_POLL_MS);
};

const syncRouteDrivenResultsContextForIntent = async (
  intent: SparkIntent,
  options: { preserveLoadedDays: boolean },
): Promise<void> => {
  await syncIntentMetadataForResultIntent(intent);

  let immediateSnapshot: SparkIntentResultsSnapshot | null = null;

  if (activeResultsIntentId.value !== intent.id) {
    immediateSnapshot = await resolveImmediateSnapshotForIntent(intent);
    sparkStore.applyActiveIntentResultsSnapshot(immediateSnapshot, immediateSnapshot.intent.id);
  }

  const canonicalIntent = resolveIntentById(intent.id) || immediateSnapshot?.intent || intent;

  if (!options.preserveLoadedDays) {
    if (immediateSnapshot) {
      setLoadedResultDaysToSingle(buildLoadedResultDaySnapshotFromStoreSnapshot(immediateSnapshot));
    } else {
      resetLoadedResultDaysToCurrent(canonicalIntent);
    }
    maybeRefreshActiveResultsContextForIntent(canonicalIntent, immediateSnapshot);
    return;
  }

  if (immediateSnapshot) {
    upsertLoadedResultDaySorted(buildLoadedResultDaySnapshotFromStoreSnapshot(immediateSnapshot));
  } else if (!findLoadedResultDayByIntentId(canonicalIntent.id)) {
    appendLoadedResultDayFromCurrent(canonicalIntent);
  }

  maybeRefreshActiveResultsContextForIntent(canonicalIntent, immediateSnapshot);
};

const resolveTocTargetIntent = async (
  requestedDate: string,
): Promise<{ intent: SparkIntent | null; resolvedTargetDate: string }> => {
  const existingPreferredIntent = pickPreferredIntentForDate(requestedDate);

  if (existingPreferredIntent) {
    return {
      intent: existingPreferredIntent,
      resolvedTargetDate: requestedDate,
    };
  }

  const resolvedDate = await ensureDateIntentForResults(requestedDate);

  if (!resolvedDate) {
    return {
      intent: null,
      resolvedTargetDate: requestedDate,
    };
  }

  return {
    intent: pickPreferredIntentForDate(resolvedDate),
    resolvedTargetDate: resolvedDate,
  };
};

const handleLoadPreviousResultDay = async (options?: {
  intent?: SparkIntent;
  allowOutsideResults?: boolean;
  collapseToIntent?: boolean;
}) => {
  if (!options?.allowOutsideResults && sparkStep.value !== SparkStep.RESULTS) {
    return;
  }

  if (isLoadingPreviousResultDay.value) {
    return;
  }

  const intentToPrepend = options?.intent || previousResultIntent.value;

  if (!intentToPrepend) {
    return;
  }

  isLoadingPreviousResultDay.value = true;
  previousResultDayError.value = null;

  try {
    const snapshot = await sparkStore.fetchIntentResultsSnapshot(intentToPrepend, {
      forceRefresh: options?.collapseToIntent,
      silent: true,
    });
    const nextDay = buildLoadedResultDaySnapshotFromStoreSnapshot(snapshot);

    if (options?.collapseToIntent) {
      sparkStore.applyActiveIntentResultsSnapshot(snapshot, intentToPrepend.id);
      setLoadedResultDaysToSingle(nextDay);
    } else {
      upsertLoadedResultDaySorted(nextDay);
    }

    await nextTick();
  } catch (error) {
    previousResultDayError.value = error instanceof Error ? error.message : 'Failed to load previous day results.';
  } finally {
    isLoadingPreviousResultDay.value = false;
  }
};

const handleLoadNextResultDay = async (options?: {
  intent?: SparkIntent;
  allowOutsideResults?: boolean;
  collapseToIntent?: boolean;
}) => {
  if (!options?.allowOutsideResults && sparkStep.value !== SparkStep.RESULTS) {
    return;
  }

  if (isLoadingNextResultDay.value || (!options?.collapseToIntent && isLoading.value)) {
    return;
  }

  const intentToAppend = options?.intent || nextResultIntent.value;

  if (!intentToAppend) {
    return;
  }

  isLoadingNextResultDay.value = true;

  try {
    const snapshot = await sparkStore.fetchIntentResultsSnapshot(intentToAppend, {
      forceRefresh: options?.collapseToIntent,
      silent: true,
    });
    const nextDay = buildLoadedResultDaySnapshotFromStoreSnapshot(snapshot);

    if (options?.collapseToIntent) {
      sparkStore.applyActiveIntentResultsSnapshot(snapshot, intentToAppend.id);
      setLoadedResultDaysToSingle(nextDay);
    } else {
      appendLoadedResultDayShell(intentToAppend);
      await nextTick();
      upsertLoadedResultDaySorted(nextDay);
    }

    await nextTick();
  } catch (error) {
    logger.warn('Failed to load next day results', {
      error,
      intentId: intentToAppend.id,
      intentDate: intentToAppend.date,
    });
  } finally {
    isLoadingNextResultDay.value = false;
  }
};

const resolveFetchingResultsSource = (
  requestedDate: string,
  fallbackSource: FetchingResultsSource,
): FetchingResultsSource => {
  const pendingRequest = pendingFetchingResultsRoute.value;

  if (pendingRequest?.date === requestedDate) {
    pendingFetchingResultsRoute.value = null;
    return pendingRequest.source;
  }

  return fallbackSource;
};

const handleFetchingResultsRoute = async (requestedDate: string, source: FetchingResultsSource = 'toc') => {
  const clampedRequestedDate = clampResultsDateToVisibleWindow(requestedDate);
  const resolvedSource = resolveFetchingResultsSource(clampedRequestedDate, source);

  if (isFetchingResultsRouteInFlight.value) {
    queuedFetchingResultsRoute.value = {
      date: clampedRequestedDate,
      source: resolvedSource,
    };
    return;
  }

  isFetchingResultsRouteInFlight.value = true;

  try {
    let nextRequestToProcess: { date: string; source: FetchingResultsSource } | null = {
      date: clampedRequestedDate,
      source: resolvedSource,
    };

    while (nextRequestToProcess) {
      const { date: dateToProcess, source: sourceToProcess } = nextRequestToProcess;
      queuedFetchingResultsRoute.value = null;

      const prevBoundaryIntent = previousResultIntent.value;
      const nextBoundaryIntent = nextResultIntent.value;
      const { intent, resolvedTargetDate } = await resolveTocTargetIntent(dateToProcess);

      if (hasLeftSparks()) return;

      if (!intent) {
        await redirectToCreatedIntentResults(resolvedTargetDate);
        nextRequestToProcess = queuedFetchingResultsRoute.value;
        continue;
      }

      if (intent.date !== resolvedTargetDate) {
        logger.warn('Fetching route target mismatch; redirecting to resolved date', {
          requestedDate: dateToProcess,
          resolvedTargetDate,
          intentId: intent.id,
          intentDate: intent.date,
        });
        await redirectToCreatedIntentResults(resolvedTargetDate);
        nextRequestToProcess = queuedFetchingResultsRoute.value;
        continue;
      }

      await showWizardLoader();

      if (hasLeftSparks()) return;

      await syncIntentMetadataForResultIntent(intent);

      if (hasLeftSparks()) return;

      if (prevBoundaryIntent?.id === intent.id) {
        await handleLoadPreviousResultDay({
          intent,
          allowOutsideResults: true,
          collapseToIntent: true,
        });
      } else if (nextBoundaryIntent?.id === intent.id) {
        await handleLoadNextResultDay({
          intent,
          allowOutsideResults: true,
          collapseToIntent: true,
        });
      } else {
        const snapshot = await sparkStore.fetchIntentResultsSnapshot(intent, {
          forceRefresh: true,
          silent: true,
        });
        sparkStore.applyActiveIntentResultsSnapshot(snapshot, intent.id);
        setLoadedResultDaysToSingle(buildLoadedResultDaySnapshotFromStoreSnapshot(snapshot));
      }

      if (hasLeftSparks()) return;

      const resolvedCardToken = resolveTransitionAnchorCardToken(intent, sourceToProcess);
      pendingTocTransientViewClearDate.value = null;
      await router.push({
        name: RouterNameType.SPARKS,
        query: buildResultsQuery(intent.date, { viewToken: resolvedCardToken }),
      });
      nextRequestToProcess = queuedFetchingResultsRoute.value;
    }
  } finally {
    isScrollLocked.value = false;
    pendingFetchingResultsRoute.value = null;
    queuedFetchingResultsRoute.value = null;
    isFetchingResultsRouteInFlight.value = false;
  }
};

const pushFetchingResultsRoute = async (requestedDate: string, source: FetchingResultsSource) => {
  const clampedDate = clampResultsDateToVisibleWindow(requestedDate);
  const isAlreadyFetchingRoute = route.query.date === clampedDate && activeRouteViewToken.value === 'fetching';

  if (isAlreadyFetchingRoute) {
    await handleFetchingResultsRoute(clampedDate, source);
    return;
  }

  pendingFetchingResultsRoute.value = {
    date: clampedDate,
    source,
  };

  await router.push({
    name: RouterNameType.SPARKS,
    query: {
      ...route.query,
      date: clampedDate,
      view: 'fetching',
    },
  });
};

const handleResultDayTocClick = async (date: string) => {
  setCanonicalResultURL.cancel();
  isURLBeingMemoized = false;
  clearVotedCardHold();
  sparkStore.clearPriorityProposalId();
  clearMissingProposalState();
  hasStaleTocResults.value = false;

  if (date === WEEK_TOC_DATE) {
    const weekCard = resultDeck.value.find(isWeekCard);
    if (weekCard) {
      activeSnapIndex.value = resultDeck.value.indexOf(weekCard);
      await scrollResultsToCard(weekCard, { behavior: 'instant' });
    }
    void refreshWeekPreview();
    return;
  }

  pendingTocTransientViewClearDate.value = null;
  await pushFetchingResultsRoute(date, 'toc');
};

const ensureResultIntentContext = async (intent: SparkIntent) => {
  const routeDate = typeof route.query.date === 'string' ? route.query.date : undefined;
  const isRouteDateAligned = routeDate === intent.date;
  const isPayloadOwnerAligned = activeResultsIntentId.value === intent.id;

  if (isRouteDateAligned && isPayloadOwnerAligned) {
    if (sparkIntent.value?.id !== intent.id) {
      await syncIntentMetadataForResultIntent(intent);
    }

    return;
  }

  await syncRouteDrivenResultsContextForIntent(intent, {
    preserveLoadedDays: true,
  });
};

const reloadLinkedProposalResult = async () => {
  const pendingMissingProposal = missingProposalState.value;

  if (!pendingMissingProposal) {
    return;
  }

  upsertMissingProposalState({
    proposalId: pendingMissingProposal.proposalId,
    intentId: pendingMissingProposal.intentId,
    date: pendingMissingProposal.date,
    phase: 'result_reloading',
  });

  try {
    const existingIntent = intents.value.find((intent) => intent.id === pendingMissingProposal.intentId) || null;
    const resolvedIntent = existingIntent || (await sparkStore.resolveIntentByDate(pendingMissingProposal.date));

    if (!resolvedIntent) {
      upsertMissingProposalState({
        proposalId: pendingMissingProposal.proposalId,
        intentId: pendingMissingProposal.intentId,
        date: pendingMissingProposal.date,
        phase: 'missing',
        reloadError: 'Could not load intent for this proposal date.',
      });
      return;
    }

    sparkStore.setPriorityProposalId(pendingMissingProposal.proposalId, resolvedIntent.id);
    await Promise.all([
      sparkStore.findIntentAndGambitResults(resolvedIntent.id),
      proposalStore.fetchProposal(pendingMissingProposal.proposalId),
    ]);
    resetLoadedResultDaysToCurrent(resolvedIntent);
    await nextTick();

    if (!isLinkedProposalPresentInResults(pendingMissingProposal.proposalId)) {
      upsertMissingProposalState({
        proposalId: pendingMissingProposal.proposalId,
        intentId: resolvedIntent.id,
        date: resolvedIntent.date,
        phase: 'missing',
        reloadError: 'Proposal is still unavailable. Try reloading again shortly.',
      });
      return;
    }

    clearMissingProposalState();
  } catch (error) {
    upsertMissingProposalState({
      proposalId: pendingMissingProposal.proposalId,
      intentId: pendingMissingProposal.intentId,
      date: pendingMissingProposal.date,
      phase: 'missing',
      reloadError: error instanceof Error ? error.message : 'Could not reload proposal results.',
    });
  }
};

const handleCreateOrJoinProposalForIntent = async (
  intent: SparkIntent,
  segment: SegmentResult,
  startTime: string,
  endTime: string,
  chessSetCount = 0,
) => {
  noteSelfTocAction();
  await ensureResultIntentContext(intent);

  await handleCreateOrJoinProposal(segment, startTime, endTime, chessSetCount);
};

const handleAcceptInvitationForIntent = async (intent: SparkIntent, proposalId: string, chessSetCount = 0) => {
  const activeCard = resultDeck.value[activeSnapIndex.value];
  noteSelfTocAction();
  isJoiningProposal.value = true;
  suppressResultDaySync = true;
  await ensureResultIntentContext(intent);

  const response = await sparkStore.acceptProposalInvitation(proposalId, chessSetCount);

  if (response?.data?.isSuccessful) {
    clearVotedCardHold();
    await sparkStore.fetchIntents({ refreshExisting: true });
    isJoiningProposal.value = false;
    evaluateUnseenTocChanges();
    return;
  }

  if (response?.data?.id) {
    holdVotedCardInPlace(response.data.id);

    if (activeCard?.result?.segment) {
      mutateLoadedResultDayForCreatedProposal(response.data, activeCard.result.segment, activeCard);
    }
  } else {
    suppressResultDaySync = false;
  }

  isJoiningProposal.value = false;
  evaluateUnseenTocChanges();
};

const handleLeaveProposalForIntent = async (intent: SparkIntent, proposalId: string, segment: SegmentResult) => {
  noteSelfTocAction();
  await ensureResultIntentContext(intent);
  if (heldVoteProposalId.value === proposalId) {
    clearVotedCardHold();
  }
  await handleLeaveProposal(proposalId, segment);
  void proposalStore.fetchProposal(proposalId);
  evaluateUnseenTocChanges();
};

const handleMeetAnywaysForIntent = async (
  intent: SparkIntent,
  proposalId: string,
  _segment: SegmentResult,
): Promise<boolean> => {
  noteSelfTocAction();
  isJoiningProposal.value = true;
  suppressResultDaySync = true;
  await ensureResultIntentContext(intent);
  const result = await sparkStore.meetAnyways(proposalId);

  if (result) {
    patchLoadedResultDayProposal(result);
  }

  if (result?.isSuccessful) {
    await sparkStore.fetchIntents({ refreshExisting: true });
    isJoiningProposal.value = false;
    evaluateUnseenTocChanges();
    return true;
  }

  suppressResultDaySync = false;
  isJoiningProposal.value = false;
  evaluateUnseenTocChanges();
  return !!result;
};

const handleUndoMeetAnywaysForIntent = async (
  intent: SparkIntent,
  proposalId: string,
  _segment: SegmentResult,
): Promise<boolean> => {
  noteSelfTocAction();
  await ensureResultIntentContext(intent);
  const result = await sparkStore.undoMeetAnyways(proposalId);

  if (result) {
    patchLoadedResultDayProposal(result);
  }

  evaluateUnseenTocChanges();
  return !!result;
};

const handleCreateCustomProposalForIntent = async (
  intent: SparkIntent,
  newProposal: CreateProposalDto,
): Promise<CustomProposalCreateResult> => {
  const fallbackMessage = 'Could not create proposal. Please try again.';

  try {
    const selectedIntent =
      intents.value.find((candidateIntent) => candidateIntent.id === newProposal.intentId) || intent;

    await ensureResultIntentContext(selectedIntent);
    const result = await handleCreateCustomProposal(newProposal, selectedIntent.date);

    if (!result.error) {
      suppressResultDaySync = true;
    }

    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : fallbackMessage;

    logger.warn('Failed to create custom proposal for intent', {
      intentId: intent.id,
      newProposal,
      error,
    });

    return {
      error: {
        message: message || fallbackMessage,
      },
    };
  }
};

const isSameSegmentIdentity = (left: SegmentResult, right: SegmentResult): boolean => {
  return left.location.id === right.location.id && left.start === right.start && left.end === right.end;
};

const mutateLoadedResultDayForCreatedProposal = (
  proposal: SegmentProposal,
  sourceSegment: SegmentResult,
  sourceCard: ResultCard,
): number | null => {
  if (sourceCard.kind !== 'match' && sourceCard.kind !== 'proposal') {
    return null;
  }

  if (!sourceCard.intent || !sourceCard.result || !Number.isSafeInteger(sourceCard.resultIndex)) {
    return null;
  }

  if (!isSameSegmentIdentity(sourceCard.result.segment, sourceSegment)) {
    return null;
  }

  const loadedDayIndex = loadedResultDays.value.findIndex((day) => day.intent.id === sourceCard.intent!.id);

  if (loadedDayIndex < 0) {
    return null;
  }

  const loadedDay = loadedResultDays.value[loadedDayIndex];
  const filteredResultIndex = sourceCard.resultIndex;

  if (filteredResultIndex == null || !Number.isSafeInteger(filteredResultIndex)) {
    return null;
  }

  if (filteredResultIndex < 0 || filteredResultIndex >= loadedDay.filteredResults.length) {
    return null;
  }

  const currentEntry = loadedDay.filteredResults[filteredResultIndex];

  if (!currentEntry || !isSameSegmentIdentity(currentEntry.segment, sourceSegment)) {
    return null;
  }

  const nextFilteredResults = [...loadedDay.filteredResults];
  nextFilteredResults.splice(filteredResultIndex, 1, {
    segment: currentEntry.segment,
    proposal,
  });

  let didUpdateSnapshot = false;
  const nextSourceSnapshot = loadedDay.sourceSnapshot
    ? {
        ...loadedDay.sourceSnapshot,
        sparkResults: loadedDay.sourceSnapshot.sparkResults.map((entry) => {
          if (didUpdateSnapshot || !isSameSegmentIdentity(entry.segment, sourceSegment)) {
            return entry;
          }

          const hasSameExistingProposalId = currentEntry.proposal?.id
            ? entry.proposal?.id === currentEntry.proposal.id
            : true;

          if (!hasSameExistingProposalId) {
            return entry;
          }

          didUpdateSnapshot = true;

          return {
            segment: entry.segment,
            proposal,
          };
        }),
      }
    : null;

  const nextDays = [...loadedResultDays.value];
  nextDays.splice(loadedDayIndex, 1, {
    ...loadedDay,
    filteredResults: nextFilteredResults,
    sourceSnapshot: nextSourceSnapshot,
  });
  loadedResultDays.value = nextDays;

  return filteredResultIndex;
};

const transitionCreatedProposalInPlace = async (
  proposal: SegmentProposal,
  sourceSegment: SegmentResult,
): Promise<boolean> => {
  const currentIndex = activeSnapIndex.value;
  const currentCard = resultDeck.value[currentIndex];
  const previousLoadedResultDays = loadedResultDays.value.map((day) => ({
    ...day,
    invitedChallenges: [...day.invitedChallenges],
    invitedConfirmedGambits: [...day.invitedConfirmedGambits],
    confirmedGambits: [...day.confirmedGambits],
    filteredResults: cloneFilteredResultList(day.filteredResults),
    sourceSnapshot: day.sourceSnapshot ? cloneSparkIntentResultsSnapshot(day.sourceSnapshot) : null,
  }));

  if (!currentCard || currentCard.date !== sparkIntent.value?.date) {
    return false;
  }

  const mutatedResultIndex = mutateLoadedResultDayForCreatedProposal(proposal, sourceSegment, currentCard);

  if (mutatedResultIndex === null) {
    return false;
  }

  await nextTick();

  const nextIndex = resultDeck.value.findIndex(
    (card) =>
      card.intent?.id === currentCard.intent?.id &&
      card.date === currentCard.date &&
      card.resultIndex === mutatedResultIndex &&
      card.result?.proposal?.id === proposal.id,
  );

  if (nextIndex < 0) {
    loadedResultDays.value = previousLoadedResultDays;
    return false;
  }

  const nextCard = resultDeck.value[nextIndex];

  if (!nextCard) {
    loadedResultDays.value = previousLoadedResultDays;
    return false;
  }

  activeSnapIndex.value = nextIndex;
  sparkStep.value = SparkStep.RESULTS;
  currentScreen.value = SparkScreen.RESULTS;
  currentProposalId.value = proposal.id;
  currentGambitId.value = null;

  await scrollResultsToCard(nextCard, { behavior: 'instant' });

  setCanonicalResultURL.cancel();
  setCanonicalResultURL(nextIndex);
  void proposalStore.fetchProposal(proposal.id);

  return true;
};

const navigateToCreatedProposal = async (
  proposalId: string,
  targetDate: string,
  navigationMode: 'push' | 'replace',
): Promise<void> => {
  const fallbackDate =
    (typeof route.query.date === 'string' && isValidDateString(route.query.date) ? route.query.date : null) ||
    sparkIntent.value?.date ||
    null;
  const resolvedTargetDate = isValidDateString(targetDate) ? targetDate : fallbackDate;

  if (!resolvedTargetDate) {
    logger.warn('Could not resolve target date for created proposal navigation.', {
      proposalId,
      targetDate,
      navigationMode,
    });
    return;
  }

  await router[navigationMode]({
    name: RouterNameType.SPARKS,
    query: buildResultsQuery(resolvedTargetDate, { proposalId }),
  });
};

const normalizeProposalGambitTypes = (types?: GambitType[]) => {
  if (!types?.length) {
    return [GambitType.ANY];
  }

  const normalized = types.filter((type) => ![GambitType.STREAMED, GambitType.TIMED].includes(type));
  return normalized.length ? normalized : [GambitType.ANY];
};

interface ProposedGambitDto {
  intent: SparkIntent;
  locationId: string;
  startTime: Date;
  gambitType: GambitType[];
  ownerChessSetCount?: number;
  title?: string;
}

const handleCreateProposedGambit = async ({
  intent,
  locationId,
  startTime,
  gambitType,
  ownerChessSetCount,
  title,
}: ProposedGambitDto) => {
  const endTime = addHours(startTime!, 2);

  const payload: CreateProposalDto = {
    start: format(startTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    end: format(endTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    locationId,
    intentId: intent.id,
    gambitType: normalizeProposalGambitTypes(gambitType),
    ownerChessSetCount: ownerChessSetCount ?? 0,
    title: title || undefined,
  };

  isJoiningProposal.value = true;
  suppressResultDaySync = true;

  try {
    const result = await sparkStore.createProposal(payload);

    if (sparkStore.gambitConflictId) {
      return {
        error: {
          gambitId: sparkStore.gambitConflictId,
          message: 'A gambit already exists at this location during this time.',
        },
      };
    }

    if (!result) {
      return { error: { message: 'Could not create proposal.' } };
    }

    return { data: { proposal: result } };
  } finally {
    suppressResultDaySync = false;
    isJoiningProposal.value = false;
  }
};

const handleCreateOrJoinProposal = async (
  segment: SegmentResult,
  startTime: string,
  endTime: string,
  chessSetCount = 0,
) => {
  noteSelfTocAction();
  isJoiningProposal.value = true;
  suppressResultDaySync = true;

  const result = await sparkStore.createProposal({
    start: format(startTime, 'yyyy-MM-dd HH:mm'),
    end: format(endTime, 'yyyy-MM-dd HH:mm'),
    locationId: segment.location.id,
    intentId: sparkIntent.value!.id,
    ownerChessSetCount: chessSetCount,
  });

  if (sparkStore.gambitConflictId) {
    clearVotedCardHold();
    isJoiningProposal.value = false;
    await openGambitExistsModal();
    return;
  }

  if (result?.id) {
    holdVotedCardInPlace(result.id);
    const didTransitionInPlace = await transitionCreatedProposalInPlace(result, segment);

    if (!didTransitionInPlace) {
      clearVotedCardHold();
      await navigateToCreatedProposal(result.id, sparkIntent.value!.date, 'replace');
    }
  } else {
    suppressResultDaySync = false;
  }

  isJoiningProposal.value = false;
  evaluateUnseenTocChanges();
};

const handleLeaveProposal = async (proposalId: string, segment: SegmentResult) => {
  return sparkStore.leaveProposal(proposalId, segment);
};

const handleCreateCustomProposal = async (
  newProposal: CreateProposalDto,
  targetDate?: string,
): Promise<CustomProposalCreateResult> => {
  const fallbackMessage = 'Could not create proposal. Please try again.';

  try {
    if (!sparkIntent.value) {
      return {
        error: { message: fallbackMessage },
      };
    }

    const { locationId, start, gambitType, ownerChessSetCount, title } = newProposal;

    const result = await handleCreateProposedGambit({
      intent: sparkIntent.value,
      locationId,
      gambitType: gambitType!,
      startTime: new Date(start),
      ownerChessSetCount,
      title,
    });

    if (result.error) {
      if (result.error.gambitId) {
        sparkStore.gambitConflictId = result.error.gambitId;
        await openGambitExistsModal();
        return {};
      }
      return {
        error: {
          message: result.error.message || fallbackMessage,
        },
      };
    }

    if (!result.data) {
      return {
        error: { message: fallbackMessage },
      };
    }

    const { proposal } = result.data;

    if (!proposal.id) {
      return {
        error: { message: fallbackMessage },
      };
    }

    await navigateToCreatedProposal(proposal.id, targetDate || sparkIntent.value.date, 'push');
    await sparkStore.findIntentAndGambitResults(sparkIntent.value?.id);

    return { proposalId: proposal.id };
  } catch (error) {
    const message = error instanceof Error ? error.message : fallbackMessage;

    logger.warn('Failed to create custom proposal', {
      newProposal,
      targetDate,
      error,
    });

    return {
      error: {
        message: message || fallbackMessage,
      },
    };
  }
};

// Live snap sync

const handleEnteredSlide = async (index: number) => {
  if (liveSnapIndex.value === index) {
    return;
  }

  liveSnapIndex.value = index;

  if (index <= 1 && showNewResultsAbove.value) {
    showNewResultsAbove.value = false;
    firstChangedCardToken.value = null;
  }

  const liveCard = resultDeck.value[index];
  if (!liveCard) {
    return;
  }

  if (liveCard.kind === 'prev_loader' || liveCard.kind === 'next_loader') {
    return;
  }
};

// Results snap + URL sync

const handleSnappedSlide = async (index: number) => {
  if (!Number.isSafeInteger(index) || index < 0 || index >= resultDeck.value.length) {
    return;
  }

  setCanonicalResultURL.cancel();

  const previousCard = activeResultCard.value;
  const wasProgrammaticScroll = isProgrammaticResultsScroll.value;
  activeSnapIndex.value = index;
  const card = resultDeck.value[index];

  if (!card) {
    return;
  }

  if (!wasProgrammaticScroll) {
    recordSwipeHintUserSnap();
  }

  const didChangeResultToc =
    !wasProgrammaticScroll &&
    !!previousCard &&
    previousCard.kind !== 'prev_loader' &&
    previousCard.kind !== 'next_loader' &&
    card.kind !== 'prev_loader' &&
    card.kind !== 'next_loader' &&
    !isWeekCard(previousCard) &&
    previousCard.date !== card.date;

  if (isWeekCard(card)) {
    clearVotedCardHold();

    if (wasProgrammaticScroll) {
      clearProgrammaticResultsScrollGuard();
    }

    await syncLiveResultCardAfterSnap(index);
    return;
  }

  if (didChangeResultToc) {
    clearVotedCardHold();
    hasStaleTocResults.value = false;

    const source: FetchingResultsSource = previousCard.date < card.date ? 'next_boundary' : 'prev_boundary';
    await pushFetchingResultsRoute(card.date, source);
    return;
  }

  if (card.kind === 'prev_loader') {
    clearVotedCardHold();

    if (wasProgrammaticScroll) {
      clearProgrammaticResultsScrollGuard();
    }

    if (isLoadingPreviousResultDay.value || isFetchingResultsRouteInFlight.value) {
      return;
    }

    const prependedIntent = card.intent;

    if (!prependedIntent) {
      return;
    }

    isScrollFrozen.value = true;

    try {
      await pushFetchingResultsRoute(prependedIntent.date, 'prev_boundary');
    } finally {
      await nextTick();
      isScrollFrozen.value = false;
    }

    return;
  }

  if (card.kind === 'next_loader') {
    clearVotedCardHold();

    if (wasProgrammaticScroll) {
      clearProgrammaticResultsScrollGuard();
    }

    if (isLoadingNextResultDay.value || isFetchingResultsRouteInFlight.value) {
      return;
    }

    const appendedIntent = card.intent;

    if (!appendedIntent) {
      return;
    }

    isScrollFrozen.value = true;

    try {
      await pushFetchingResultsRoute(appendedIntent.date, 'next_boundary');
    } finally {
      await nextTick();
      isScrollFrozen.value = false;
    }

    return;
  }

  const resolvedIndex = index;
  const resolvedCard = resultDeck.value[resolvedIndex] || card;

  if (resolvedCard.intent && resolvedCard.intent.id !== previousCard?.intent?.id) {
    syncActiveIntentFromLoadedResultDay(resolvedCard.intent);
  }

  if (wasProgrammaticScroll) {
    clearProgrammaticResultsScrollGuard();
  }

  if (isLinkableCard(resolvedCard)) {
    setCanonicalResultURL(resolvedIndex);
  }

  await syncLiveResultCardAfterSnap(resolvedIndex);
};

const syncLiveResultCardAfterSnap = async (index: number): Promise<void> => {
  if (!Number.isSafeInteger(index) || index < 0 || index >= resultDeck.value.length) {
    currentProposalId.value = null;
    currentGambitId.value = null;
    return;
  }

  const card = resultDeck.value[index];

  if (!card) {
    currentProposalId.value = null;
    currentGambitId.value = null;
    return;
  }

  const proposalId = card.result?.proposal?.id;
  const gambitId = card.gambit?.id;

  if (proposalId && (card.kind === 'proposal' || card.kind === 'match')) {
    if (currentProposalId.value !== proposalId) {
      await proposalStore.fetchProposal(proposalId);
    }

    currentProposalId.value = proposalId;
    currentGambitId.value = null;
    return;
  }

  if (gambitId && card.kind === 'gambit') {
    currentGambitId.value = gambitId;
    currentProposalId.value = null;
    return;
  }

  currentProposalId.value = null;
  currentGambitId.value = null;
};

function isLinkableCard(card: ResultCard) {
  return (
    card.kind === 'gambit' ||
    card.kind === 'proposal' ||
    card.kind === 'match' ||
    card.kind === 'new' ||
    card.kind === 'disabled'
  );
}

let isURLBeingMemoized = false;
let lastSparksQuery: Record<string, string> = {};

const isSameResultsCameraQuery = (query: { date?: unknown; view?: unknown }): boolean => {
  const currentDate = typeof route.query.date === 'string' ? route.query.date : undefined;
  const nextDate = typeof query.date === 'string' ? query.date : undefined;
  const nextView = typeof query.view === 'string' ? query.view : undefined;

  return currentDate === nextDate && activeRouteViewToken.value === normalizeRouteViewToken(nextView);
};

const memoizeResultsQuery = async (date: string, viewToken?: string | null) => {
  const canonicalQuery = buildResultsQuery(date, { viewToken });

  if (isSameResultsCameraQuery(canonicalQuery)) {
    isURLBeingMemoized = false;
    lastSparksQuery = { ...canonicalQuery } as Record<string, string>;
    return;
  }

  isURLBeingMemoized = true;
  lastSparksQuery = { ...canonicalQuery } as Record<string, string>;

  try {
    await router.replace({
      name: RouterNameType.SPARKS,
      query: canonicalQuery,
    });

    if (isSameResultsCameraQuery(canonicalQuery)) {
      isURLBeingMemoized = false;
    }
  } catch (error) {
    isURLBeingMemoized = false;
    logger.warn('Failed to memoize results URL', {
      date,
      viewToken,
      error,
    });
  }
};

const setCanonicalResultURL = debounce(
  async (index) => {
    if (!isKeepAliveActive.value || route.name !== RouterNameType.SPARKS) return;

    const card = resultDeck.value[index];
    if (!card) return;

    if (!card || !isLinkableCard(card)) {
      return;
    }

    const resolvedToken = normalizeRouteViewToken(card.token);

    if (!resolvedToken) {
      return;
    }

    const canonicalQuery = buildResultsQuery(card.date, { viewToken: resolvedToken });

    if (isSameResultsCameraQuery(canonicalQuery)) {
      isURLBeingMemoized = false;
      return;
    }

    isURLBeingMemoized = true;
    lastSparksQuery = { ...canonicalQuery } as Record<string, string>;

    try {
      await router.replace({
        name: RouterNameType.SPARKS,
        query: canonicalQuery,
      });

      if (isSameResultsCameraQuery(canonicalQuery)) {
        isURLBeingMemoized = false;
      }
    } catch (error) {
      isURLBeingMemoized = false;
      logger.warn('Failed to memoize canonical result URL', {
        card: card,
        token: resolvedToken,
        error,
      });
    }
  },
  600,
  { trailing: true },
);

async function scrollWizardToLoader(options?: ScrollToOptions) {
  const wizardSlides = document.querySelector('.wizard-slides');
  const loaderSlide = document.querySelector('#loader');

  if (!wizardSlides || !loaderSlide) return;

  scrollTo(wizardSlides, loaderSlide, options);
}

const showWizardLoader = async () => {
  sparkStep.value = SparkStep.WIZARD;
  currentScreen.value = SparkScreen.DAYS;
  isEditAllowed.value = false;
  isPlacesAllowed.value = false;
  isScrollLocked.value = true;
  await nextTick();
  await scrollWizardToLoader({ behavior: 'instant' });
};

// Lifecycle & side-effects

let hasBeenMounted = false;

onMounted(async () => {
  sparkStore.clear();
  handleNowTime();

  await authStore.fetchUserInfo(true);
  hasBeenMounted = true;
});

const isKeepAliveRestoring = ref(false);
let savedResultsScrollTop: number | null = null;
/* The Week card has no date, so the URL still names the last day card; this
   carries "was on the week" across a keep-alive trip instead. */
const wasWeekCardActiveOnLeave = ref(false);

const resolveKeepAliveLandingDate = (candidateDate?: string): string => {
  if (candidateDate && isValidDateString(candidateDate) && isDateWithinVisibleResultsWindow(candidateDate)) {
    return candidateDate;
  }

  return defaultResultsDateKey.value;
};

const recoverBareKeepAliveLanding = async (): Promise<void> => {
  handleNowTime();
  await nextTick();

  const restoredDateRaw = (typeof route.query.date === 'string' && route.query.date) || lastSparksQuery.date || null;
  const restoredDate = restoredDateRaw && isValidDateString(restoredDateRaw) ? restoredDateRaw : null;
  const leftoverDateIsGone = !!restoredDate && !isDateWithinVisibleResultsWindow(restoredDate);

  if (leftoverDateIsGone) {
    const landingDate = defaultResultsDateKey.value;
    const landingIntent =
      pickPreferredIntentForDate(landingDate) ||
      findEnabledIntentByDate(intents.value, landingDate) ||
      earliestEnabledIntent.value;

    if (landingIntent) {
      await handleResumeIntentDay(landingIntent);
    } else {
      await redirectToFallbackResults(landingDate);
    }

    return;
  }

  const weekCard = wasWeekCardActiveOnLeave.value ? resultDeck.value.find(isWeekCard) : null;
  wasWeekCardActiveOnLeave.value = false;

  if (weekCard) {
    activeSnapIndex.value = resultDeck.value.indexOf(weekCard);
    await scrollResultsToCard(weekCard, { behavior: 'instant' });
    return;
  }

  const cameraDate = restoredDate || sparkIntent.value?.date || loadedResultDays.value[0]?.intent.date;
  const fallbackIndex = cameraDate ? findFallbackDeckIndex(cameraDate) : -1;
  const fallbackCard = fallbackIndex >= 0 ? resultDeck.value[fallbackIndex] : null;

  if (fallbackCard && !isSkeletonResultCard(fallbackCard)) {
    activeSnapIndex.value = fallbackIndex;
    await scrollResultsToCard(fallbackCard, { behavior: 'instant' });
  }

  if (!route.query.date && lastSparksQuery.date) {
    const restoredView =
      lastSparksQuery.view === 'fetching' || !lastSparksQuery.view
        ? fallbackCard && !isSkeletonResultCard(fallbackCard)
          ? fallbackCard.token
          : resolveFallbackCardTokenForDate(lastSparksQuery.date)
        : lastSparksQuery.view;

    await router.replace({
      name: RouterNameType.SPARKS,
      query: {
        ...lastSparksQuery,
        view: restoredView && restoredView !== 'fetching' ? restoredView : undefined,
      },
    });
  }
};

onActivated(async () => {
  const incomingView = typeof route.query.view === 'string' ? route.query.view : undefined;
  const incomingDate = typeof route.query.date === 'string' ? route.query.date : undefined;
  const shouldSeekDirectCard = isExplicitSparksCardOpen() && isDirectCardViewToken(incomingView);

  const container = document.getElementById('results');
  if (container) {
    if (savedResultsScrollTop !== null && !shouldSeekDirectCard) {
      container.scrollTop = savedResultsScrollTop;
      savedResultsScrollTop = null;
    } else {
      savedResultsScrollTop = null;
    }
    container.style.visibility = '';
  }

  isKeepAliveActive.value = true;
  isFetchingResultsRouteInFlight.value = false;
  queuedFetchingResultsRoute.value = null;
  pendingFetchingResultsRoute.value = null;

  if (!hasBeenMounted) return;

  const incomingCardToken = incomingView && isDirectCardViewToken(incomingView) ? incomingView : undefined;
  const canLandOnExistingCard =
    !!incomingDate &&
    isValidDateString(incomingDate) &&
    !!incomingCardToken &&
    (isAlreadyShowingCard(incomingDate, incomingCardToken) ||
      findLiveDeckCardForToken(incomingDate, incomingCardToken) !== null);

  if (shouldSeekDirectCard && !canLandOnExistingCard) {
    isKeepAliveRestoring.value = true;
  }

  const currentCityId = city.value?.id || null;
  const didCityChange = lastActiveCityId !== null && currentCityId !== lastActiveCityId;

  if (didCityChange) {
    isCitySwitchLoading.value = true;
    clearLoadedResultDays();
    intentStore.clearIntentStore();
    chosenLocationIds.value = [];
    isDiscoveryBootstrapInFlight.value = false;
    isRefreshResultsInFlight.value = false;
    discoveryBootstrapPromise = null;
    lastSparksQuery = {};
    lastActiveCityId = currentCityId;
  }

  const isStale = !sparkIntent.value || loadedResultDays.value.length === 0 || !activeResultsIntentId.value;

  if (isStale) {
    sparkStep.value = SparkStep.WIZARD;
    currentScreen.value = SparkScreen.DAYS;
    isKeepAliveRestoring.value = true;
    handleNowTime();
    authStore.fetchUserInfo(true);

    try {
      await bootstrapDiscoveryData();

      if (!isKeepAliveActive.value || route.name !== RouterNameType.SPARKS) return;

      const staleDateRaw = typeof route.query.date === 'string' ? route.query.date : lastSparksQuery.date;
      const staleView = typeof route.query.view === 'string' ? route.query.view : lastSparksQuery.view;
      const staleDate = resolveKeepAliveLandingDate(staleDateRaw);
      const staleProposalId = staleView?.startsWith('proposal:') ? staleView.slice('proposal:'.length) : undefined;
      const staleGambitId = staleView?.startsWith('gambit:') ? staleView.slice('gambit:'.length) : undefined;
      const canResumeStaleCard =
        staleDateRaw &&
        isValidDateString(staleDateRaw) &&
        isDateWithinVisibleResultsWindow(staleDateRaw) &&
        staleView !== 'fetching';

      if (shouldIgnoreLeftoverCameraView(staleView) || staleView === 'fetching') {
        const leftoverIntent =
          findEnabledIntentByDate(intents.value, staleDate) || enabledIntentsByDate.value[0] || null;

        if (leftoverIntent) {
          await handleResumeIntentDay(leftoverIntent);
        } else {
          await redirectToFallbackResults(staleDate);
        }
      } else if (canResumeStaleCard && staleProposalId) {
        await handleResumeIntentProposal(staleDateRaw, staleProposalId);
      } else if (canResumeStaleCard && staleGambitId) {
        const gambitIntent = findEnabledIntentByDate(intents.value, staleDateRaw);
        if (gambitIntent) {
          await handleResumeIntentGambit(gambitIntent, staleGambitId);
        } else {
          await redirectToFallbackResults(staleDate);
        }
      } else {
        const fallbackIntent =
          findEnabledIntentByDate(intents.value, staleDate) || enabledIntentsByDate.value[0] || null;

        if (fallbackIntent) {
          await handleResumeIntentDay(fallbackIntent);
        } else {
          await redirectToFallbackResults(staleDate);
        }
      }
    } finally {
      isKeepAliveRestoring.value = false;
      isCitySwitchLoading.value = false;
    }

    return;
  }

  if (!didCityChange) {
    const hasIncomingParams = Boolean(incomingDate || incomingView);
    const isExternalDeepLink =
      hasIncomingParams &&
      (shouldSeekDirectCard ||
        incomingView !== lastSparksQuery.view ||
        (incomingDate && incomingDate !== lastSparksQuery.date));

    const deepLinkProposalId = incomingView?.startsWith('proposal:')
      ? incomingView.slice('proposal:'.length)
      : undefined;
    const deepLinkGambitId = incomingView?.startsWith('gambit:') ? incomingView.slice('gambit:'.length) : undefined;
    const hasCardDeepLink = isExternalDeepLink && (!!deepLinkProposalId || !!deepLinkGambitId);

    if (hasCardDeepLink) {
      if (incomingDate && incomingCardToken && (await seekExistingCardInDeck(incomingDate, incomingCardToken))) {
        return;
      }

      isKeepAliveRestoring.value = true;
      handleNowTime();
      authStore.fetchUserInfo(true);

      try {
        await sparkStore.fetchIntents();
        await sparkStore.ensureIntentsForInvitationDates();

        if (!isKeepAliveActive.value || route.name !== RouterNameType.SPARKS) return;

        if (deepLinkProposalId && incomingDate && isValidDateString(incomingDate)) {
          await handleResumeIntentProposal(incomingDate, deepLinkProposalId);
        } else if (deepLinkGambitId && incomingDate && isValidDateString(incomingDate)) {
          const gambitIntent = findEnabledIntentByDate(intents.value, incomingDate);
          if (gambitIntent) {
            await handleResumeIntentGambit(gambitIntent, deepLinkGambitId);
          } else {
            await redirectToFallbackResults(defaultResultsDateKey.value);
          }
        }
      } finally {
        isKeepAliveRestoring.value = false;
      }

      return;
    }

    sparkStep.value = SparkStep.RESULTS;
    currentScreen.value = SparkScreen.RESULTS;

    if (!isExternalDeepLink) {
      isKeepAliveRestoring.value = true;
      await recoverBareKeepAliveLanding();
    } else {
      const activeCard = resultDeck.value[activeSnapIndex.value];
      if (activeCard && container) {
        const target = document.getElementById(activeCard.domId);
        if (target) {
          container.scrollTop = target.offsetTop;
        }
      }
    }
  }

  await nextTick();

  handleNowTime();

  const hadSparkableLocations = hasSparkableLocations.value;
  await authStore.fetchUserInfo(true);

  if (didCityChange || (!hadSparkableLocations && hasSparkableLocations.value)) {
    await sparkStore.ensureDailyAvailabilitiesExtended(city.value?.id);
  }

  try {
    await sparkStore.fetchIntents();
    await sparkStore.ensureIntentsForInvitationDates();

    const isStaleIntent = sparkIntent.value && !intents.value.some((intent) => intent.id === sparkIntent.value?.id);

    if (isStaleIntent) {
      const firstIntent = earliestEnabledIntent.value || intents.value[0];
      if (firstIntent) {
        await activateIntent(firstIntent);
      } else {
        intentStore.clearIntentStore();
      }
    } else if (!sparkIntent.value && intents.value.length) {
      const firstIntent = earliestEnabledIntent.value || intents.value[0];
      if (firstIntent) {
        await activateIntent(firstIntent);
      }
    } else if (sparkIntent.value && isKeepAliveActive.value) {
      maybeRefreshActiveResultsContextForIntent(sparkIntent.value, null);
      if (sparkStore.droppedGambitId) {
        removeGambitFromLoadedDays(sparkStore.droppedGambitId);
      }
      if (sparkStore.droppedProposalId) {
        removeProposalFromLoadedDays(sparkStore.droppedProposalId);
      }
      if (sparkStore.mergedProposalGambitId && sparkStore.droppedProposalId) {
        handleMergedProposal(sparkStore.droppedProposalId, sparkStore.mergedProposalGambitId);
      }
      if (sparkStore.gambitDeckRefreshId) {
        refreshLoadedDayForGambit(sparkStore.gambitDeckRefreshId);
      }
      syncDayProposalListeners();
      syncDayGambitListeners();
      startUnseenTocPoll();
    }
  } finally {
    isKeepAliveRestoring.value = false;
  }
});

onDeactivated(() => {
  isKeepAliveActive.value = false;
  teardownUnseenTocWatchers();
  setCanonicalResultURL.cancel();
  lastActiveCityId = city.value?.id || null;
  wasWeekCardActiveOnLeave.value = isWeekCardActive.value;

  const container = document.getElementById('results');
  if (container) {
    savedResultsScrollTop = container.scrollTop;
    container.style.visibility = 'hidden';
  }

  if (nowTimeout) {
    clearTimeout(nowTimeout);
    nowTimeout = null;
  }
});

const buildResultsQuery = (
  date: string,
  options?: {
    card?: ResultCard;
    proposalId?: string;
    gambitId?: string;
    viewToken?: string | null;
  },
) => {
  let resolvedToken;

  const { proposalId, gambitId, viewToken, card } = options || {};

  if (card) {
    resolvedToken = card.token;
  } else if (proposalId) {
    resolvedToken = getProposalCardToken(proposalId);
  } else if (gambitId) {
    resolvedToken = getGambitCardToken(gambitId);
  } else if (viewToken) {
    resolvedToken = viewToken;
  }

  const { from: _from, ...queryWithoutFrom } = route.query;
  return {
    ...queryWithoutFrom,
    date,
    view: normalizeRouteViewToken(resolvedToken),
  };
};

const isCreatingFallbackIntent = ref<boolean>(false);

const formatWallDateTime = (date: string, hour: number): string => {
  return `${date}T${hour.toString().padStart(2, '0')}:00:00.000`;
};

const normalizeHourRange = (startHour: number, endHour: number): { startHour: number; endHour: number } => {
  const safeStart = Math.max(0, Math.min(23, startHour));
  const safeEnd = Math.max(0, Math.min(23, endHour));

  if (safeEnd > safeStart) {
    return {
      startHour: safeStart,
      endHour: safeEnd,
    };
  }

  return {
    startHour: safeStart,
    endHour: Math.min(23, safeStart + 1),
  };
};

const getFallbackHourRange = (date: string): { startHour: number; endHour: number } => {
  const sameDayIntent = intents.value.find((intent) => intent.date === date);

  if (sameDayIntent) {
    const tz = sameDayIntent.timeZone || 'UTC';
    const startH = Number.parseInt(formatInTimeZone(sameDayIntent.timeSpan.start, tz, 'H'), 10);
    const endH = Number.parseInt(formatInTimeZone(sameDayIntent.timeSpan.end, tz, 'H'), 10);

    return normalizeHourRange(startH, endH);
  }

  if (earliestEnabledIntent.value) {
    const tz = earliestEnabledIntent.value.timeZone || 'UTC';
    const startH = Number.parseInt(formatInTimeZone(earliestEnabledIntent.value.timeSpan.start, tz, 'H'), 10);
    const endH = Number.parseInt(formatInTimeZone(earliestEnabledIntent.value.timeSpan.end, tz, 'H'), 10);

    return normalizeHourRange(startH, endH);
  }

  return {
    startHour: DEFAULT_FALLBACK_START_HOUR,
    endHour: DEFAULT_FALLBACK_END_HOUR,
  };
};

const getFallbackLocationIds = (): string[] => {
  if (chosenLocationIds.value.length > 0) {
    return [...chosenLocationIds.value];
  }

  if (sparkIntent.value?.locationIds?.length) {
    return [...sparkIntent.value.locationIds];
  }

  const firstIntentWithLocations = intents.value.find((intent) => intent.locationIds?.length);
  return firstIntentWithLocations?.locationIds ? [...firstIntentWithLocations.locationIds] : [];
};

const getFallbackCoordinates = () => {
  return city.value?.coordinates || sparkIntent.value?.city?.coordinates || null;
};

const ensureDateIntentForResults = async (requestedDate: string): Promise<string | null> => {
  const targetDate = clampResultsDateToVisibleWindow(requestedDate);
  const existingEnabledIntent = findEnabledIntentByDate(intents.value, targetDate);

  if (existingEnabledIntent) {
    return existingEnabledIntent.date;
  }

  const sameDateIntent = intents.value.find((intent) => intent.date === targetDate);

  if (sameDateIntent?.isDisabled) {
    return sameDateIntent.date;
  }

  if (!city.value?.id || !city.value?.coordinates) {
    await cityStore.determineCity();
  }

  if (!city.value?.id || !city.value?.coordinates) {
    return null;
  }

  const { startHour, endHour } = getFallbackHourRange(targetDate);

  const createResult = await sparkStore.findOrCreateIntent({
    cityId: city.value.id,
    locationIds: getFallbackLocationIds(),
    start: formatWallDateTime(targetDate, startHour),
    end: formatWallDateTime(targetDate, endHour),
    coordinates: getFallbackCoordinates() || city.value.coordinates,
    radiusMeters: sparkIntent.value?.radiusMeters || DEFAULT_FALLBACK_RADIUS_METERS,
    mapZoom: DEFAULT_FALLBACK_MAP_ZOOM,
  });

  if (!createResult.data) {
    return null;
  }

  return createResult.data.date;
};

const redirectToCreatedIntentResults = async (requestedDate: string, proposalId?: string) => {
  if (isCreatingFallbackIntent.value) {
    return;
  }

  isCreatingFallbackIntent.value = true;

  try {
    const resolvedDate = await ensureDateIntentForResults(requestedDate);

    if (hasLeftSparks()) return;

    if (resolvedDate) {
      return router.push({
        name: RouterNameType.SPARKS,
        query: buildResultsQuery(resolvedDate, { proposalId }),
      });
    }

    if (earliestEnabledIntent.value) {
      const clampedEarliestDate = clampResultsDateToVisibleWindow(earliestEnabledIntent.value.date);
      return router.push({
        name: RouterNameType.SPARKS,
        query: buildResultsQuery(clampedEarliestDate, { proposalId }),
      });
    }

    const fallbackDate = clampResultsDateToVisibleWindow(requestedDate);
    return router.push({
      name: RouterNameType.SPARKS,
      query: buildResultsQuery(fallbackDate, { proposalId }),
    });
  } finally {
    isCreatingFallbackIntent.value = false;
  }
};

const redirectToFallbackResults = async (requestedDate: string) => {
  if (hasLeftSparks()) return;

  const preferredIntent = enabledIntentsByDate.value.find(
    (intent) => isDateWithinVisibleResultsWindow(intent.date) && intent.date >= requestedDate,
  );

  const anyVisibleIntent =
    preferredIntent || enabledIntentsByDate.value.find((intent) => isDateWithinVisibleResultsWindow(intent.date));

  if (anyVisibleIntent) {
    return router.push({
      name: RouterNameType.SPARKS,
      query: buildResultsQuery(anyVisibleIntent.date),
    });
  }

  return redirectToCreatedIntentResults(clampResultsDateToVisibleWindow(requestedDate));
};

const syncResultsDeckFromRoute = (params: { date?: string; view?: string }) => {
  if (params.view === 'fetching') {
    return;
  }

  if (isFetchingResultsRouteInFlight.value) {
    return;
  }

  const requestedToken = activeRouteViewToken.value;

  if (!params.date || !isValidDateString(params.date)) {
    return;
  }

  if (!isDateWithinVisibleResultsWindow(params.date)) {
    return;
  }

  const directIndex = requestedToken ? findDeckIndexForRouteCard(params.date, requestedToken) : -1;
  const hasAnyCardForRequestedDate = resultDeck.value.some((card) => !isWeekCard(card) && card.date === params.date);

  if (requestedToken && directIndex < 0 && hasAnyCardForRequestedDate) {
    // TODO: Retry once against refreshed route-driven results, then show dedicated not-found handling / proposal_missing.
    return;
  }

  if (directIndex < 0 && !hasAnyCardForRequestedDate) {
    return;
  }

  const resolvedIndex = directIndex >= 0 ? directIndex : findFallbackDeckIndex(params.date);

  if (resolvedIndex < 0) {
    return;
  }

  const resolvedCard = resultDeck.value[resolvedIndex];

  // TODO: Leave this in place for later refactor (Merge URL navigation)
  if (resolvedCard && sparkStep.value === SparkStep.RESULTS && !isProgrammaticResultsScroll.value) {
    activeSnapIndex.value = resolvedIndex;
    void scrollResultsToCard(resolvedCard, {
      behavior: 'instant',
    });
  }
};

// Params

const dateParam = computed(() => (typeof route.query.date === 'string' ? route.query.date : undefined));

// Note: Unified UI Router / Route watcher

watch(
  () => ({
    dateParam: route.query.date,
    viewParam: route.query.view,
    fromParam: route.query.from,
    actionParam: route.query.action,
    intentsLoading: isLoadingIntents.value,
    minVisibleDateKey: minVisibleResultsDateKey.value,
    isLoadingPreviousResultDay: isLoadingPreviousResultDay.value,
    isLoadingNextResultDay: isLoadingNextResultDay.value,
  }),
  async (params, prevParams) => {
    if (!isKeepAliveActive.value || route.name !== RouterNameType.SPARKS) return;

    if (isKeepAliveRestoring.value) return;

    const date = params.dateParam ? String(params.dateParam) : undefined;
    const view = params.viewParam ? String(params.viewParam) : undefined;
    const isFetchingDayRoute = view === 'fetching';

    if (isRefreshResultsInFlight.value && !isFetchingDayRoute) {
      return;
    }

    if (isApplyingAvailabilityResults) {
      return;
    }

    if (isURLBeingMemoized === true) {
      isURLBeingMemoized = false;
      if (!isFetchingDayRoute) {
        return;
      }
    }

    if (date) {
      const { from: _fromRoute, ...routeQueryWithoutFrom } = route.query;
      lastSparksQuery = { ...routeQueryWithoutFrom } as Record<string, string>;
    }

    syncResultsDeckFromRoute({
      date,
      view,
    });

    const didExplicitCardOpen =
      isExplicitSparksCardOpen({ from: params.fromParam, action: params.actionParam }) &&
      (!prevParams || !isExplicitSparksCardOpen({ from: prevParams.fromParam, action: prevParams.actionParam }));

    const didRouteFlowInputsChange =
      !prevParams ||
      params.dateParam !== prevParams.dateParam ||
      params.viewParam !== prevParams.viewParam ||
      didExplicitCardOpen ||
      params.intentsLoading !== prevParams.intentsLoading ||
      params.minVisibleDateKey !== prevParams.minVisibleDateKey;

    if (!didRouteFlowInputsChange) {
      return;
    }

    if (isInitializing.value) {
      await bootstrapDiscoveryData();

      isInitializing.value = false;

      if (!isKeepAliveActive.value || route.name !== RouterNameType.SPARKS) return;
    }

    if (params.intentsLoading) {
      return;
    }

    const proposalId = view?.startsWith('proposal:') ? view.slice('proposal:'.length) : undefined;
    const gambitId = view?.startsWith('gambit:') ? view.slice('gambit:'.length) : undefined;
    const isNew = view === 'new';

    if (!(proposalId && date && isValidDateString(date))) {
      clearMissingProposalState();
    }

    if (proposalId && (!date || !isValidDateString(date))) {
      sparkStore.clearPriorityProposalId();
      return redirectToFallbackResults(defaultResultsDateKey.value);
    }

    // Date means we're creating, editing or browsing an Intent
    if (date && isValidDateString(date)) {
      const normalizedCardToken = normalizeRouteViewToken(view);
      const nextIntent = findEnabledIntentByDate(intents.value, date);
      const newDateIntent = findIntentByDate(intents.value, date);
      const targetResultIntent = newDateIntent || nextIntent;
      const hasAnyCardForRequestedDate = resultDeck.value.some((card) => card.date === date);
      const directDeckIndex = normalizedCardToken ? findDeckIndexForRouteCard(date, normalizedCardToken) : -1;
      const activePayloadIntent = activeResultsIntentId.value ? resolveIntentById(activeResultsIntentId.value) : null;
      const isSameLoadedDay = activePayloadIntent?.date === date;
      const hasHydratedTargetIntent = Boolean(
        targetResultIntent &&
          activeResultsIntentId.value === targetResultIntent.id &&
          findLoadedResultDayByIntentId(targetResultIntent.id),
      );

      if (shouldIgnoreLeftoverCameraView(view)) {
        sparkStore.clearPriorityProposalId();

        if (nextIntent) {
          return handleResumeIntentDay(nextIntent);
        }
      }

      if (proposalId) {
        const proposalToken = getProposalCardToken(proposalId);
        if (await seekExistingCardInDeck(date, proposalToken)) {
          return;
        }

        const bootstrapKey = buildProposalBootstrapKey(date, proposalId);
        if (proposalBootstrapInFlightKey.value === bootstrapKey) {
          return;
        }
        return handleResumeIntentProposal(date, proposalId);
      }

      if (!isDateWithinVisibleResultsWindow(date)) {
        const clampedDate = clampResultsDateToVisibleWindow(date);
        sparkStore.clearPriorityProposalId();
        return redirectToCreatedIntentResults(clampedDate);
      }

      if (normalizedCardToken === 'fetching') {
        const loadedContentCard = findPrioritizedResultCardForDate(date);

        if (loadedContentCard) {
          const loadedContentIndex = resultDeck.value.findIndex((card) => card.key === loadedContentCard.key);
          sparkStep.value = SparkStep.RESULTS;
          currentScreen.value = SparkScreen.RESULTS;
          activeSnapIndex.value = Math.max(loadedContentIndex, 0);
          await scrollResultsToCard(loadedContentCard, { behavior: 'instant' });
          return;
        }

        return handleFetchingResultsRoute(date);
      }

      if (
        sparkStep.value === SparkStep.WIZARD &&
        normalizedCardToken &&
        normalizedCardToken !== 'fetching' &&
        targetResultIntent &&
        hasHydratedTargetIntent &&
        directDeckIndex >= 0
      ) {
        await prepareResultsDeckBeforeWizardExit(targetResultIntent, normalizedCardToken);
        if (hasLeftSparks()) return;
        sparkStep.value = SparkStep.RESULTS;
        currentScreen.value = SparkScreen.RESULTS;
        syncActiveIntentFromLoadedResultDay(targetResultIntent);
        return;
      }

      if (
        sparkStep.value === SparkStep.RESULTS &&
        normalizedCardToken &&
        normalizedCardToken !== 'fetching' &&
        isSameLoadedDay
      ) {
        if (directDeckIndex >= 0) {
          if (directDeckIndex !== activeSnapIndex.value) {
            activeSnapIndex.value = directDeckIndex;
            const targetCard = resultDeck.value[directDeckIndex];
            if (targetCard) {
              await scrollResultsToCard(targetCard, { behavior: 'instant' });
            }
          }
          return;
        }

        if (gambitId && hasAnyCardForRequestedDate) {
          const fallbackIdx = findFallbackDeckIndex(date);
          if (fallbackIdx >= 0) {
            activeSnapIndex.value = fallbackIdx;
            const fallbackCard = resultDeck.value[fallbackIdx];
            if (fallbackCard) {
              await scrollResultsToCard(fallbackCard, { behavior: 'instant' });
            }
          }
          return;
        }

        if (!gambitId && hasAnyCardForRequestedDate) {
          return;
        }
      }

      if (isNew && newDateIntent) {
        const hasPreloadedResultDay = Boolean(findLoadedResultDayByIntentId(newDateIntent.id));

        if (hasPreloadedResultDay && activeResultsIntentId.value !== newDateIntent.id) {
          await syncRouteDrivenResultsContextForIntent(newDateIntent, {
            preserveLoadedDays: true,
          });
          return;
        }
      }

      if (!nextIntent) {
        const disabledIntent = findIntentByDate(intents.value, date);

        if (disabledIntent?.isDisabled) {
          sparkStore.clearPriorityProposalId();
          return handleResumeIntentDay(disabledIntent, normalizedCardToken || undefined);
        }

        sparkStore.clearPriorityProposalId();
        return redirectToCreatedIntentResults(date);
      }

      const alreadyLoadedContentCard = findPrioritizedResultCardForDate(date);

      if (alreadyLoadedContentCard && nextIntent) {
        const alreadyLoadedContentIndex = resultDeck.value.findIndex(
          (card) => card.key === alreadyLoadedContentCard.key,
        );
        sparkStep.value = SparkStep.RESULTS;
        currentScreen.value = SparkScreen.RESULTS;
        activeSnapIndex.value = Math.max(alreadyLoadedContentIndex, 0);
        syncActiveIntentFromLoadedResultDay(nextIntent);
        await scrollResultsToCard(alreadyLoadedContentCard, { behavior: 'instant' });
        return;
      }

      if (gambitId) {
        // Go to Intent Results screen
        return handleResumeIntentGambit(nextIntent, gambitId);
      } else if (isNew) {
        // Go to Intent Results screen
        return handleResumeIntentExact(nextIntent);
      }

      // Go to Intent Results screen
      return handleResumeIntentDay(nextIntent, normalizedCardToken || undefined);
    }

    if (!date || !isValidDateString(date)) {
      return redirectToFallbackResults(defaultResultsDateKey.value);
    }
  },
  { immediate: true },
);

// Utility

function getLatestSparkProposal(
  oldEntry?: SegmentProposal | null,
  newEntry?: SegmentProposal | null,
): SegmentProposal | null {
  if (!oldEntry) return null;
  if (!newEntry) return oldEntry;
  if (newEntry.id !== oldEntry.id) return oldEntry;
  return newEntry;
}
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.SparkIgnition {
  /* Single width for the card and the day TOC, so their edges line up by
     construction at every viewport instead of being kept in sync by hand. */
  --spark-card-inset: 1rem;
  --spark-card-w: min(calc(100svw - 2 * var(--spark-card-inset)), 400px);
  /* One top for the day TOC in every context, safe-area aware — same anchor the
     walk toolbar uses. The city label hangs off it so the pair moves together. */
  --spark-toc-top: calc(var(--sai-top) + 12px);
  /* Derived from the nav rather than hand-tuned: the card sits directly on this
     reserve, so a nav that grows without it here would touch the card. */
  --spark-nav-reserve: calc(var(--safe-bottom-offset) + var(--screen-nav-height) + 1rem);

  background: linear-gradient(175deg, oklch(0.28 0.035 var(--hue)) 0%, var(--color-dark-700) 45%), var(--color-dark-700);
  background-attachment: fixed;
  position: relative;
  width: 100%;
  height: 100%;
}

.city-switch-loader {
  height: 100%;
}

.ScreenNav {
  --screen-nav-z: 1000;
}

.nav-action-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 3rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.has-unread {
    background: var(--color-pink-20);
    border-color: var(--color-dark-900);
    color: var(--color-white);
  }
  @include hover-capable {
    &:hover.has-unread {
      background: var(--color-pink-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }
  }

  @include press;
}

/* Sits in the gap between the card bottom and the fixed nav row. */
.swipe-hint {
  position: fixed;
  left: 50%;
  bottom: calc(var(--safe-bottom-offset) + 5.5rem);
  transform: translateX(-50%);
  z-index: 999;
  pointer-events: none;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-white-40);
  white-space: nowrap;
}

.swipe-hint-fade-enter-active,
.swipe-hint-fade-leave-active {
  transition: opacity 200ms ease;
}

.swipe-hint-fade-enter-from,
.swipe-hint-fade-leave-to {
  opacity: 0;
}

.SparkIgnition:not(.is-mobile) :deep(.result-slide) {
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.SparkIgnition:not(.is-mobile) :deep(.result-slide .results-card-layout > *) {
  position: relative;
}

.section-header {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 112;
}

.results-day-toc {
  position: absolute;
  top: var(--spark-toc-top);
  left: 50%;
  transform: translateX(-50%);
  z-index: 115;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: var(--spark-card-w);
  user-select: none;
  overflow: hidden;
}

.results-day-toc.is-loading {
  pointer-events: none;
}

.results-day-toc-days {
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

/* Narrow screens cannot fit the Week chip, seven days and the availability
   button, so only the days scroll and the chips on either side stay pinned. */
@media (max-width: 380px) {
  .results-day-toc-days {
    justify-content: flex-start;
    gap: 0.25rem;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .results-day-toc-item {
      scroll-snap-align: start;
      flex-shrink: 0;
    }
  }
}

.results-day-toc-week {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 3px;
  min-height: 2.75rem;
  margin-right: 0.25rem;
  padding: 0.2rem 0.4rem;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: var(--color-white-100);
  text-shadow: 0 1px 10px rgb(0 0 0 / 0.35);
  white-space: nowrap;
  cursor: pointer;
  /* Matches the dimming of unselected day chips (tocItemOpacity). */
  opacity: 0.55;

  &.has-activity:not(.is-active) {
    opacity: 1;
    color: var(--color-pink);
  }

  &.is-active {
    opacity: 1;
    background: var(--color-white-20);
  }
}

.spark-city-label {
  position: absolute;
  top: calc(var(--spark-toc-top) + 3rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 110;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  pointer-events: auto;
}

.spark-city-label-text {
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--color-white-60);
  white-space: nowrap;
  cursor: pointer;
}

.spark-city-search {
  width: 200px;
}

.results-day-toc-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: var(--color-white-100);
  text-shadow: 0 1px 10px rgb(0 0 0 / 0.35);
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  border-radius: 0.5rem;
  white-space: nowrap;
}

.toc-day-letter {
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.08em;
  font-weight: 400;
  text-transform: uppercase;
}

.toc-day-number {
  font-size: 16px;
  line-height: 1;
  letter-spacing: 0;
  font-weight: 600;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.results-day-toc-item.is-active .toc-day-number {
  background: var(--color-white-20);
  color: var(--color-white-100);
}
@include hover-capable {
  .results-day-toc:not(.is-mobile) .results-day-toc-item:not(.is-active):not(.is-disabled):hover {
    filter: brightness(1.4);
  }
}

.results-day-toc-item.is-disabled {
  color: var(--color-white-30);
}

.is-mobile .results-day-toc-item {
  padding: 0.15rem 0.2rem;
}

.is-mobile .toc-day-letter {
  font-size: 10px;
}

.is-mobile .toc-day-number {
  font-size: 15px;
}

.new-results-above {
  position: absolute;
  bottom: 5.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 115;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: var(--color-white-100);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  text-shadow: 0 1px 4px rgb(0 0 0 / 0.3);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;

  &:active {
    transform: translateX(-50%) scale(0.96);
  }
}

.new-results-fade-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.new-results-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.new-results-fade-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(0.5rem);
}
.new-results-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(0.5rem);
}

.results-day-toc-availability {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  pointer-events: auto;
  margin-left: 0.25rem;

  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}

.days-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
  gap: 2rem;
}

/* The single reserve for the day TOC above and the nav buttons below, and the
   size container the card reads its available height from. Unscoped: the nav
   buttons sit at the same offset in every context, so a breakpoint fork here
   only shifted the card's centre line across the mobile/desktop boundary.
   No horizontal padding — the card's own width token carries the edge inset. */
.spark-card-slide {
  padding-block: 4.25rem var(--spark-nav-reserve);
  container-type: size;
}

.is-mobile .wizard-slide {
  padding: 4.25rem var(--spark-card-inset) var(--spark-nav-reserve);
}

/* Width is the shared TOC width. Height is min(available, 1.5×width) so the card
   is exactly 2/3 when there is room (PWA, desktop) and only shortens when browser
   chrome eats vertical space — it never gives up width. All card types share this. */
.spark-card {
  width: var(--spark-card-w);
  max-width: 100%;
  height: min(100cqh, calc(var(--spark-card-w) * 1.5));
  flex: none;

  /* Own size container so internals can use min(cqw, cqh) and stay compact when
     the card goes wider-and-shorter. Only descendants may read these. */
  container-type: size;
  /* At true 2/3 (h = 1.5w), min(Xcqw, X/1.5 cqh) equals Xcqw — same as before.
     When the card is squat, the cqh term wins and internals stay compact. */
  --card-avatar-size: clamp(2.75rem, min(15cqw, 10cqh), 4rem);
  --card-badge-size: clamp(0.95rem, min(5.5cqw, 3.67cqh), 1.25rem);
  /* One stacked avatar is badge slot + avatar + rating slot. Reserving that full
     height on every seat keeps empty rings on the same centre line as avatars,
     rather than dropping to the bottom-anchored row's floor. */
  --seat-slot: max(var(--card-badge-size), 16px);
  --seat-height: calc(var(--card-avatar-size) + 2 * var(--seat-slot) + 0.4rem);
  /* Held to a constant share of the card so the frame does not read as thicker
     on a small card. Floored at the body text size, since a gutter looks cramped
     relative to the text beside it rather than relative to the card. */
  --card-gutter: clamp(1rem, min(5.25cqw, 3.5cqh), 1.375rem);
  --card-stack-gap: clamp(0.5rem, min(3cqw, 2cqh), 0.875rem);
  /* Seats sit as far from the cover's bottom edge as from its left edge. */
  --card-foot-inset: var(--card-gutter);
  /* One chip row between cover and meta, reserved on every card (empty or not)
     so covers stay the same height across the deck. */
  --card-band-height: 22px;
  /* Shared meta band — applied on each card's `.meta` (descendant) so `cqw`
     resolves against this size container. Ideal at 2/3: half width minus gap
     (cover stays square). Floor covers CustomCard's densest controls. */
  --card-meta-min: 10.75rem;
  --card-play-size: clamp(3rem, min(17.7cqw, 11.8cqh), 3.5rem);
  --card-play-icon: clamp(1.3rem, min(7.6cqw, 5.07cqh), 1.5rem);
  --card-meet-size: clamp(2.5rem, min(14.5cqw, 9.7cqh), 2.9rem);
  --card-meet-icon: clamp(1.15rem, min(6.6cqw, 4.4cqh), 1.3rem);
}

.day-placeholder-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
  padding: 2rem;
  border-radius: var(--radius-normal);
  background: radial-gradient(circle at 20% 15%, var(--color-white-10), transparent 50%),
    radial-gradient(circle at 80% 85%, var(--color-pink-20), transparent 55%), var(--color-card-surface);
}

.day-placeholder-eyebrow {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-white-50);
}

.day-placeholder-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--color-white-100);
}

.day-placeholder-subtitle {
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-white-40);
}

.empty-reload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 17rem);
  gap: 0.45rem;
}

.empty-reload-card .day-placeholder-subtitle {
  text-transform: none;
  letter-spacing: 0.01em;
  line-height: 1.3;
  max-width: 15rem;
  text-wrap: balance;
  margin-bottom: 1.5rem;
}

#places {
  position: relative;
}

.loading-slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

#edit :deep(.EditSpark .edit-layout) {
  max-width: 480px;
  flex-shrink: 0;
}

#wizard {
  position: absolute;
}

#wizard.is-active {
}

#wizard:not(.is-active) {
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms;
}

#results {
  position: absolute;
  z-index: 109;
  overscroll-behavior: contain;
}

.results-card-layout {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

#results:not(.is-active) {
  opacity: 0;
  pointer-events: none;
}

.loader-slide {
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-mobile .wizard-slide {
  padding-right: 1rem;
}

.wizard-slide.is-not-rendered {
  display: none;
}
</style>

<style lang="scss">
#gambits-app.is-mobile .wizard-slides {
  width: 100svw;
  height: 100svh;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}

#gambits-app:not(.is-mobile) .wizard-slides {
  width: 100%;
  height: 100svh;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  z-index: 110;
}

#gambits-app.is-mobile .wizard-slide {
  width: 100svw;
  height: 100svh;
}

#gambits-app:not(.is-mobile) .wizard-slide {
  width: 100%;
  height: 100svh;
}

#gambits-app .spark-slides {
  width: 100%;
  height: 100svh;
}

#gambits-app.is-mobile .spark-slides {
  width: 100%;
}

#gambits-app .slide {
  width: 100%;
  height: 100svh;
}
</style>
